/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CustomFileUploader from "@/components/custom/CustomFileUploader";
import PlantInput from "@/components/fields/PlantInput";
import { projectStatus as projectStatusEnum } from "@/constants/constants";
import { buildFundingDisplayState } from "@/dashboard/components/project/tabs/helpers/soldFundingDisplay";
import { useFetchData } from "@/hooks/useFetchData";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import { useAuth } from "@/auth/hooks/useAuth";
import {
  FundingField,
  type FundingItem,
  type FundingMode,
  normalizeFundingValue,
} from "@/modules/drawn-funding/FundingField";
import DrawnMapField, {
  DrawnMapValue,
  normalizeDrawnValue,
  serializeDrawnAnswer,
} from "@/modules/drawn-map/DrawnMapField";
import {
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ChevronRight,
  Clock3,
  AlertTriangle,
  CheckSquare,
  Download,
  FileSpreadsheet,
  FolderOpen,
  Loader,
  Save,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

const ProjectFundingGuidedFlow = lazy(
  () => import("@/dashboard/components/project/components/financement/ProjectFundingGuidedFlow"),
);

type ApiField = {
  id: number;
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: unknown;
  visibilityMode?: "all" | "show_only" | "hide_only";
  visibilityProjectTypeIds?: number[];
  nativeField?: string | null;
  step: number;
};

type ApiSection = {
  id: number;
  name: string;
  label: string;
  description?: string | null;
  fields: ApiField[];
  step?: number;
};

type ApiStep = {
  id: number;
  step: number;
  name: string;
  label: string;
  description?: string | null;
  stepDescription?: string | null;
  options?: unknown;
  visibilityMode?: "all" | "show_only" | "hide_only";
  visibilityProjectTypeIds?: number[];
  fields: ApiField[];
  sections?: ApiSection[];
};

type DraftCurrentResponse = {
  draftId: number;
  projectId?: number | null;
  programId?: number | null;
  name?: string | null;
  projectName?: string | null;
  schema: ApiStep[];
  answers: Record<string, any>;
  status?: number | string | null;
  scoringSummary?: {
    autoScore?: number | null;
    autoMaxScore?: number | null;
    autoPercentage?: number | null;
    manualPendingMaxScore?: number | null;
    manualPendingCount?: number | null;
    manualIncludedCount?: number | null;
    manualReviewedCount?: number | null;
    totalCurrentScore?: number | null;
    totalIncludedMaxScore?: number | null;
    currentPercentage?: number | null;
    finalPercentage?: number | null;
    breakdown?: Array<{
      fieldId: number;
      mode?: string | null;
      points?: number | null;
      maxPoints?: number | null;
      pendingReview?: boolean | null;
      includedInPercentage?: boolean | null;
    }>;
  } | null;
  scoringDecision?: {
    decision?: string | null;
    decisionMode?: string | null;
    status?: number | null;
    percentage?: number | null;
  } | null;
  scoringSettings?: {
    enabled?: boolean;
    decisionMode?: string | null;
    acceptancePercentage?: number | null;
    rejectionPercentage?: number | null;
  } | null;
  manualEvaluationCompleted?: boolean | null;
};

type SidebarProgram = {
  id: number;
  name: string;
  chercheurFond?: boolean | null;
  dateFin?: string | null;
};

type SidebarData = {
  availablePrograms: SidebarProgram[];
};

type ProgramInfo = {
  id: number;
  name: string;
  dateFin?: string | null;
};

type ProjectType = {
  id: number;
  name: string;
  projectTypeLangs?: Array<{
    name?: string;
    lang?: { code?: string };
  }>;
};

type Country = {
  id: number;
  name: string;
  currency?: {
    id?: number;
    code?: string;
    symbol?: string;
    name?: string;
  } | null;
};

type Devise = {
  id: number;
  name?: string;
  code?: string;
  symbol?: string;
};

type SelectOption = {
  value: string;
  label: string;
};

type MapFieldValue = {
  drawn: DrawnMapValue;
  parcel: any[];
  location: any[];
};

const normalize = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const sanitizeAsciiFilename = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getFileNameFromDisposition = (
  disposition: string | null,
  fallback: string,
) => {
  const asciiFallback = sanitizeAsciiFilename(fallback);
  if (!disposition) return asciiFallback;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = disposition.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] ?? asciiFallback;
};

type CandidateProjectExportModule = "descriptif";

const CANDIDATE_PROJECT_EXPORT_MODULE_OPTIONS: Array<{
  value: CandidateProjectExportModule;
  title: string;
  description: string;
}> = [
  {
    value: "descriptif",
    title: "Descriptif du projet",
    description:
      "Le contenu textuel du descriptif, les financements et les reponses du formulaire dynamique, hors images.",
  },
];

const downloadCandidateProjectExport = async (
  projectId: number,
  format: "xlsx" | "docx",
  modules: CandidateProjectExportModule[],
) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/project/${projectId}/export/${format}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        modules,
        syntheticSnapshot: null,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`Erreur lors de l'export ${format === "xlsx" ? "Excel" : "Word"} du projet`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    `Rapport - Projet - export-${projectId}.${format}`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const CandidateProjectExportDialog = ({
  isOpen,
  onOpenChange,
  isExporting,
  onExport,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isExporting: boolean;
  onExport: (
    format: "xlsx" | "docx",
    modules: CandidateProjectExportModule[],
  ) => Promise<void>;
}) => {
  const [selectedFormat, setSelectedFormat] = useState<"xlsx" | "docx">("xlsx");
  const [selectedModules, setSelectedModules] = useState<
    CandidateProjectExportModule[]
  >(CANDIDATE_PROJECT_EXPORT_MODULE_OPTIONS.map((option) => option.value));

  useEffect(() => {
    if (!isOpen) return;
    setSelectedFormat("xlsx");
    setSelectedModules(
      CANDIDATE_PROJECT_EXPORT_MODULE_OPTIONS.map((option) => option.value),
    );
  }, [isOpen]);

  const toggleModule = (
    module: CandidateProjectExportModule,
    checked: boolean,
  ) => {
    setSelectedModules((current) => {
      if (checked) {
        return current.includes(module) ? current : [...current, module];
      }

      return current.filter((item) => item !== module);
    });
  };

  const allModulesChecked =
    selectedModules.length === CANDIDATE_PROJECT_EXPORT_MODULE_OPTIONS.length;

  const toggleAllModules = (checked: boolean) => {
    setSelectedModules(
      checked
        ? CANDIDATE_PROJECT_EXPORT_MODULE_OPTIONS.map((option) => option.value)
        : [],
    );
  };

  const canExport = selectedModules.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full min-w-0 max-w-[780px] rounded-[28px] border-[#d6e2d2] bg-[linear-gradient(180deg,#fbfdf9_0%,#f2f7ef_100%)] p-0 shadow-2xl sm:min-w-0">
        <div className="overflow-hidden rounded-[28px]">
          <div className="bg-[linear-gradient(135deg,#1f4b1d_0%,#5f8e53_60%,#94bf85_100%)] px-8 py-7 text-white">
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                  <Download className="h-3.5 w-3.5" />
                  Export du projet
                </div>
                <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight text-white">
                  Exporter cette fiche projet
                </DialogTitle>
                <DialogDescription className="mt-2 max-w-[560px] text-sm leading-6 text-white/85">
                  Choisissez d&apos;abord le format, puis les modules à inclure
                  dans le rapport. Tous les modules sont cochés par défaut et la
                  carte n&apos;est pas exportée.
                </DialogDescription>
              </div>
              <div className="hidden rounded-[24px] border border-white/15 bg-white/10 p-4 md:block">
                <FileSpreadsheet className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-5 px-8 py-7">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setSelectedFormat("xlsx")}
                className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[24px] border p-5 text-left transition-all ${
                  selectedFormat === "xlsx"
                    ? "border-[#6f9a5c] bg-white shadow-[0_18px_50px_rgba(75,107,60,0.15)] ring-2 ring-[#dfead9]"
                    : "border-[#d9e4d2] bg-white/80 hover:border-[#b7c9ae] hover:bg-white"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2e5f22_0%,#8fb37f_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <IconExcel className="h-8 w-8" />
                  </div>
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="text-lg font-semibold text-[#24451f]">Excel</h3>
                  <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
                    Export du projet au format Excel, structuré par modules pour
                    faciliter la lecture, l&apos;analyse et le partage.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat("docx")}
                className={`group relative flex h-full min-h-[220px] flex-col overflow-hidden rounded-[24px] border p-5 text-left transition-all ${
                  selectedFormat === "docx"
                    ? "border-[#8fa8d5] bg-white shadow-[0_18px_50px_rgba(69,99,143,0.15)] ring-2 ring-[#e3ebfb]"
                    : "border-[#d9e4d2] bg-white/80 hover:border-[#c6d6ef] hover:bg-white"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#45638f_0%,#9ab4de_100%)]" />
                <div className="flex items-start justify-between gap-4">
                  <div className="rounded-[18px] bg-[#eef4ff] p-3 text-[#45638f]">
                    <IconWord className="h-8 w-8" />
                  </div>
                </div>
                <div className="mt-5 flex flex-1 flex-col">
                  <h3 className="text-lg font-semibold text-[#314664]">Word</h3>
                  <p className="mt-2 text-sm leading-6 text-[#66758a]">
                    Export du descriptif au format Word, avec le logo et la
                    même structure que le rapport Excel.
                  </p>
                </div>
              </button>
            </div>

            <div className="rounded-[22px] border border-[#d7e3d2] bg-white/90 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-4">
                  <div className="rounded-[16px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <CheckSquare className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#294b24]">
                      Modules à inclure dans l&apos;export
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#5f6d5c]">
                      Pour l&apos;instant, seul le module descriptif est actif
                      sur les exports candidat.
                    </p>
                  </div>
                </div>

                <label className="inline-flex items-center gap-3 rounded-full border border-[#d8e3d4] bg-[#f7faf5] px-4 py-2 text-sm font-medium text-[#3d5837]">
                  <Checkbox
                    checked={allModulesChecked}
                    onCheckedChange={(checked) => toggleAllModules(Boolean(checked))}
                    className="border-[#9bb58d] data-[state=checked]:border-[#2c5d20] data-[state=checked]:bg-[#2c5d20]"
                  />
                  Tout sélectionner
                </label>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {CANDIDATE_PROJECT_EXPORT_MODULE_OPTIONS.map((option) => {
                  const checked = selectedModules.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-[20px] border px-4 py-4 transition-all ${
                        checked
                          ? "border-[#bfd3b5] bg-[#f7fbf5] shadow-[0_8px_24px_rgba(75,107,60,0.08)]"
                          : "border-[#e2e9de] bg-white hover:border-[#cad9c3]"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(nextChecked) =>
                          toggleModule(option.value, Boolean(nextChecked))
                        }
                        className="mt-0.5 border-[#9bb58d] data-[state=checked]:border-[#2c5d20] data-[state=checked]:bg-[#2c5d20]"
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm font-semibold text-[#294b24]">
                          {option.title}
                        </span>
                        <span className="mt-1 text-sm leading-6 text-[#5f6d5c]">
                          {option.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3 border-t border-[#dde7d8] bg-white/80 px-8 py-5 sm:justify-between sm:gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-[#cfdcc9] bg-white px-5 text-[#496045] hover:bg-[#f4f8f2]"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <div className="flex flex-1 items-center gap-4">
              {isExporting ? (
                <p className="flex-1 text-center text-sm leading-5 text-[#5f6d5c]">
                  L&apos;export peut prendre un peu de temps, merci de patienter
                  pendant la génération du fichier.
                </p>
              ) : null}
              <Button
                type="button"
                className="rounded-xl bg-[linear-gradient(135deg,#21511c_0%,#5f8f51_100%)] px-6 text-white shadow-lg hover:opacity-95"
                onClick={() => void onExport(selectedFormat, selectedModules)}
                disabled={isExporting || !canExport}
              >
                {isExporting ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Export en cours
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Exporter
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const isSearchFundingNativeField = (field: ApiField) =>
  normalize(field.nativeField ?? field.name ?? "") === "searchfinancinginternal";
const isProjectTypeField = (field: ApiField) =>
  normalize(field.type ?? "") === "projecttype" ||
  normalize(field.nativeField ?? field.name ?? "") === "type";
const isIntroStep = (step: Pick<ApiStep, "name" | "label">): boolean =>
  normalize(step.name ?? "") === "introduction" ||
  normalize(step.label ?? "") === "introduction";
const isNativeFundingStep = (step: Pick<ApiStep, "name" | "label">): boolean =>
  normalize(step.name ?? "") === "financement" ||
  normalize(step.label ?? "") === "financement";
const isNativeMapStep = (step: Pick<ApiStep, "name" | "label">): boolean =>
  normalize(step.name ?? "") === "parcelle_et_localisation" ||
  normalize(step.label ?? "") === "parcelle et localisation" ||
  normalize(step.name ?? "") === "map";

type CombinedFundingValue = {
  obtained?: unknown;
  desired?: unknown;
  obtainedFunding?: unknown;
  desiredFunding?: unknown;
};

const parseMaybeFundingObject = (value: unknown) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
};

const getFundingNativeMode = (field: ApiField): FundingMode | null => {
  const native = normalize(field.nativeField ?? field.name ?? "");
  if (native.includes("obtained")) return "obtained";
  if (native.includes("desired")) return "desired";
  return null;
};

const isCombinedFundingValue = (value: unknown) => {
  const parsed = parseMaybeFundingObject(value);
  const fundingValue = parsed as CombinedFundingValue;
  return Boolean(
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    (
      Array.isArray(fundingValue.obtained) ||
      Array.isArray(fundingValue.desired) ||
      Array.isArray(fundingValue.obtainedFunding) ||
      Array.isArray(fundingValue.desiredFunding)
    )
  );
};

const getCombinedFundingRawValue = (value: unknown, mode: FundingMode) => {
  const parsed = parseMaybeFundingObject(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
  const fundingValue = parsed as CombinedFundingValue;

  if (mode === "obtained") {
    return fundingValue.obtained ?? fundingValue.obtainedFunding ?? [];
  }

  return fundingValue.desired ?? fundingValue.desiredFunding ?? [];
};

const normalizeFundingModeValue = (value: unknown, mode: FundingMode) =>
  normalizeFundingValue(getCombinedFundingRawValue(value, mode));

const hasAnyFundingValue = (value: unknown) => {
  if (isCombinedFundingValue(value)) {
    return (
      normalizeFundingModeValue(value, "obtained").length > 0 ||
      normalizeFundingModeValue(value, "desired").length > 0
    );
  }

  return normalizeFundingValue(value).length > 0;
};

const CALL_FORM_SOURCE_SCOPE_OPTION = "callFormSourceScope";
const CALL_FORM_SOURCE_PROJECT_TYPE_ID_OPTION = "callFormSourceProjectTypeId";

const extractChoiceOptions = (rawOptions: unknown): string[] | null => {
  if (Array.isArray(rawOptions)) {
    return rawOptions.map((option) => String(option));
  }
  if (
    rawOptions &&
    typeof rawOptions === "object" &&
    Array.isArray((rawOptions as any).choices)
  ) {
    return (rawOptions as any).choices.map((option: any) => String(option));
  }
  return null;
};

const extractVisibilityConfig = (rawOptions: unknown): {
  mode: "all" | "show_only" | "hide_only";
  projectTypeIds: number[];
} => {
  let source: any = rawOptions;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      source = null;
    }
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return { mode: "all", projectTypeIds: [] };
  }
  const modeRaw = String((source as any).visibilityMode ?? "all")
    .trim()
    .toLowerCase();
  const mode = modeRaw === "show_only" || modeRaw === "hide_only" ? modeRaw : "all";
  const idsRaw = (source as any).visibilityProjectTypeIds;
  const projectTypeIds = Array.isArray(idsRaw)
    ? idsRaw
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id) && id > 0)
    : [];
  return { mode, projectTypeIds };
};

const resolveVisibilityConfig = (
  modeCandidate: unknown,
  idsCandidate: unknown,
  rawOptions: unknown,
): {
  mode: "all" | "show_only" | "hide_only";
  projectTypeIds: number[];
} => {
  const fallback = extractVisibilityConfig(rawOptions);
  const modeRaw = String(modeCandidate ?? "").trim().toLowerCase();
  const mode =
    modeRaw === "show_only" || modeRaw === "hide_only" || modeRaw === "all"
      ? (modeRaw as "all" | "show_only" | "hide_only")
      : fallback.mode;
  const ids = Array.isArray(idsCandidate)
    ? idsCandidate
      .map((id: any) => Number(id))
      .filter((id: number) => Number.isFinite(id) && id > 0)
    : fallback.projectTypeIds;
  return { mode, projectTypeIds: ids };
};

const isVisibleByRule = (
  mode: "all" | "show_only" | "hide_only",
  projectTypeIds: number[],
  selectedProjectTypeId: number | null,
) => {
  if (mode === "all") return true;
  if (!selectedProjectTypeId || projectTypeIds.length === 0) {
    return mode === "hide_only";
  }
  const hasType = projectTypeIds.includes(selectedProjectTypeId);
  if (mode === "show_only") return hasType;
  return !hasType;
};

const normalizeFieldSchema = (field: ApiField): ApiField => {
  const visibility = resolveVisibilityConfig(
    field.visibilityMode,
    field.visibilityProjectTypeIds,
    field.options,
  );
  return {
    ...field,
    options: extractChoiceOptions(field.options),
    visibilityMode: visibility.mode,
    visibilityProjectTypeIds: visibility.projectTypeIds,
  };
};

const normalizeSchemaSteps = (schema: ApiStep[]): ApiStep[] => {
  const ordered = [...schema].sort((a, b) => {
    const aIsIntro = isIntroStep(a);
    const bIsIntro = isIntroStep(b);
    if (aIsIntro && !bIsIntro) return -1;
    if (!aIsIntro && bIsIntro) return 1;
    const stepDiff = (a.step ?? 0) - (b.step ?? 0);
    if (stepDiff !== 0) return stepDiff;
    return (a.id ?? 0) - (b.id ?? 0);
  });

  return ordered.map((step, index) => {
    const visibility = resolveVisibilityConfig(
      step.visibilityMode,
      step.visibilityProjectTypeIds,
      step.options,
    );
    return {
      ...step,
      step: index + 1,
      fields: sortFieldsByOrder(step.fields ?? []).map(normalizeFieldSchema),
      sections: sortSectionsByOrder(step.sections ?? []).map((section) => ({
        ...section,
        fields: sortFieldsByOrder(section.fields ?? []).map(normalizeFieldSchema),
      })),
      visibilityMode: visibility.mode,
      visibilityProjectTypeIds: visibility.projectTypeIds,
    };
  });
};

const extractAutoProjectTypeIdFromSchema = (steps: ApiStep[]): number | null => {
  for (const step of steps) {
    const options =
      step.options && typeof step.options === "object" && !Array.isArray(step.options)
        ? (step.options as Record<string, unknown>)
        : null;
    if (!options) continue;

    const scope = String(options[CALL_FORM_SOURCE_SCOPE_OPTION] ?? "").trim().toLowerCase();
    const projectTypeId = Number(options[CALL_FORM_SOURCE_PROJECT_TYPE_ID_OPTION] ?? 0);
    if (scope === "project_type" && Number.isFinite(projectTypeId) && projectTypeId > 0) {
      return projectTypeId;
    }
  }

  return null;
};

const filterSchemaByProjectType = (
  steps: ApiStep[],
  selectedProjectTypeId: number | null,
): ApiStep[] => {
  const visible = steps
    .filter((step) => {
      const visibility = resolveVisibilityConfig(
        step.visibilityMode,
        step.visibilityProjectTypeIds,
        step.options,
      );
      return isVisibleByRule(
        visibility.mode,
        visibility.projectTypeIds,
        selectedProjectTypeId,
      );
    })
    .map((step) => {
      const visibleRootFields = (step.fields ?? []).filter((field) => {
        const visibility = resolveVisibilityConfig(
          field.visibilityMode,
          field.visibilityProjectTypeIds,
          field.options,
        );
        return isVisibleByRule(
          visibility.mode,
          visibility.projectTypeIds,
          selectedProjectTypeId,
        );
      });

      const visibleSections = (step.sections ?? [])
        .map((section) => ({
          ...section,
          fields: (section.fields ?? []).filter((field) => {
            const visibility = resolveVisibilityConfig(
              field.visibilityMode,
              field.visibilityProjectTypeIds,
              field.options,
            );
            return isVisibleByRule(
              visibility.mode,
              visibility.projectTypeIds,
              selectedProjectTypeId,
            );
          }),
        }))
        .filter((section) => (section.fields ?? []).length > 0);

      return {
        ...step,
        fields: visibleRootFields,
        sections: visibleSections,
      };
    })
    .filter((step) => {
      if (isIntroStep(step)) return true;
      if (isNativeFundingStep(step) || isNativeMapStep(step)) return true;
      return (step.fields?.length ?? 0) > 0 || (step.sections?.length ?? 0) > 0;
    });

  return visible.map((step, index) => ({ ...step, step: index + 1 }));
};

const parseSearchFundingValue = (rawValue: string): boolean | null => {
  const normalizedValue = normalize(rawValue);
  if (!normalizedValue) return null;
  if (normalizedValue.includes("verdeo")) return false;
  if (normalizedValue.includes("moi meme")) return true;
  return null;
};

const formatSearchFundingValueForSelect = (
  storedValue: any,
  options: string[],
): string => {
  if (typeof storedValue === "string") return storedValue;
  if (storedValue === true) {
    return options.find((option) => {
      const normalizedOption = normalize(option);
      return normalizedOption.includes("moi meme") && !normalizedOption.includes("verdeo");
    }) ?? "";
  }
  if (storedValue === false) {
    return options.find((option) => normalize(option).includes("verdeo")) ?? "";
  }
  return "";
};

const NATIVE_DESCRIPTIF_KEYS = new Set([
  "name",
  "description",
  "type",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
  "fundrecipientname",
]);
const HIDDEN_CALL_CANDIDATE_DESCRIPTIF_KEYS = new Set([
  "name",
  "description",
  "type",
  "projecttype",
  "launchdate",
  "expectedduration",
  "country",
  "searchfinancinginternal",
]);

const asArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value["hydra:member"])) {
    return value["hydra:member"] as T[];
  }
  return [];
};

const isTruthyFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

const isFalseFlag = (value: unknown): boolean =>
  value === false || value === 0 || value === "0" || value === "false";

const sortFieldsByOrder = (fields: ApiField[]) =>
  [...fields].sort((a, b) => {
    const stepDiff = (a.step ?? 0) - (b.step ?? 0);
    if (stepDiff !== 0) return stepDiff;
    return a.id - b.id;
  });

const sortSectionsByOrder = (sections: ApiSection[]) =>
  [...sections].sort((a, b) => {
    const stepDiff = (a.step ?? 0) - (b.step ?? 0);
    if (stepDiff !== 0) return stepDiff;
    return a.id - b.id;
  });

const URL_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const hasHtmlContent = (value: string): boolean => HTML_TAG_REGEX.test(value);

const splitTrailingPunctuation = (url: string) => {
  const match = url.match(/[),.;!?]+$/);
  if (!match) return { cleanUrl: url, trailing: "" };
  const trailing = match[0];
  return {
    cleanUrl: url.slice(0, -trailing.length),
    trailing,
  };
};

const renderTextWithAutoLinks = (text: string) => {
  const lines = text.split(/\r?\n/);
  return lines.map((line, lineIndex) => {
    const parts = line.split(URL_REGEX);
    return (
      <span key={`line-${lineIndex}`}>
        {parts.map((part, partIndex) => {
          if (!part) return null;
          const isUrl = /^(https?:\/\/|www\.)/i.test(part);
          if (!isUrl) {
            return <span key={`txt-${lineIndex}-${partIndex}`}>{part}</span>;
          }

          const { cleanUrl, trailing } = splitTrailingPunctuation(part);
          const href = /^https?:\/\//i.test(cleanUrl) ? cleanUrl : `https://${cleanUrl}`;
          return (
            <span key={`url-${lineIndex}-${partIndex}`}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#3b6f2a] underline hover:text-[#2f5a22]"
              >
                {cleanUrl}
              </a>
              {trailing}
            </span>
          );
        })}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
};

const toUploadMedias = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object" && !Array.isArray(value) && value.id) {
    return [value];
  }
  return [];
};

const parseMaybeJson = (input: any) => {
  let current = input;
  for (let i = 0; i < 3; i += 1) {
    if (typeof current !== "string") break;
    try {
      current = JSON.parse(current);
    } catch {
      break;
    }
  }
  return current;
};

const toSpeciesItems = (value: any): Array<{
  name: string;
  nameFr: string;
  nameEn: string;
  id: number;
}> => {
  const parsed = parseMaybeJson(value);
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((item: any) => {
      const source = item?.species ?? item;
      const name = typeof source?.name === "string" ? source.name.trim() : "";
      if (!name) return null;
      return {
        name,
        nameFr: typeof source?.nameFr === "string" && source.nameFr.trim() ? source.nameFr.trim() : name,
        nameEn: typeof source?.nameEn === "string" && source.nameEn.trim() ? source.nameEn.trim() : name,
        id: Number(source?.id) || 0,
      };
    })
    .filter((item): item is { name: string; nameFr: string; nameEn: string; id: number } => !!item);
};

const toMapFieldValue = (value: any): MapFieldValue => {
  const parsedValue = parseMaybeJson(value);
  const drawn = normalizeDrawnValue(parsedValue);
  const safeEmpty: MapFieldValue = serializeDrawnAnswer(drawn);
  if (!parsedValue) return safeEmpty;

  if (typeof parsedValue !== "object" || parsedValue === null) {
    return safeEmpty;
  }

  const serialized = serializeDrawnAnswer(normalizeDrawnValue(parsedValue));
  const parcel = Array.isArray((parsedValue as any).parcel)
    ? (parsedValue as any).parcel
    : Array.isArray((parsedValue as any).parcels)
      ? (parsedValue as any).parcels
      : serialized.parcel;
  const location = Array.isArray((parsedValue as any).location)
    ? (parsedValue as any).location
    : serialized.location;

  return { drawn: serialized.drawn, parcel, location };
};

const getProjectStatusBadge = (
  rawStatus: number | string | null | undefined,
  options?: { submitContext?: boolean },
) => {
  const status = Number(rawStatus);
  const isSubmitContext = options?.submitContext === true;
  switch (status) {
    case projectStatusEnum.InProgress:
      return { label: "En cours", className: "bg-orangeProgress text-background" };
    case projectStatusEnum.AwaitingValidation:
      return { label: "En attente de validation", className: "bg-verdeoblue text-background" };
    case projectStatusEnum.AwaitingValidationPP:
      return {
        label: isSubmitContext ? "En attente de validation" : "En attente de votre validation",
        className: "bg-verdeoblue text-background",
      };
    case projectStatusEnum.Validated:
      return { label: "Valid\u00E9", className: "bg-verdeogreen text-background" };
    case projectStatusEnum.Rejected:
      return { label: "Rejeté", className: "bg-verdeored text-background" };
    default:
      return null;
  }
};

const formatScorePercent = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "--"
    : `${Number(value).toFixed(0)}%`;

const getScoringDecisionTheme = (
  decision: string | null | undefined,
  status: number | null | undefined,
) => {
  if (decision === "auto_accepted" || Number(status) === projectStatusEnum.Validated) {
    return {
      ring: "border-emerald-200 bg-emerald-50",
      score: "text-emerald-600",
      badge: "bg-emerald-100 text-emerald-700",
      accent: "text-emerald-700",
    };
  }
  if (decision === "auto_rejected" || Number(status) === projectStatusEnum.Rejected) {
    return {
      ring: "border-red-200 bg-red-50",
      score: "text-red-600",
      badge: "bg-red-100 text-red-700",
      accent: "text-red-700",
    };
  }
  return {
    ring: "border-sky-200 bg-sky-50",
    score: "text-sky-600",
    badge: "bg-sky-100 text-sky-700",
    accent: "text-sky-700",
  };
};

const getScoringModeLabel = (decisionMode: string | null | undefined): string => {
  switch (decisionMode) {
    case "auto_threshold":
      return "Validation ou rejet automatiques, avec évaluation manuelle si des notes restent à attribuer";
    case "auto_total":
      return "Validation ou rejet automatiques après prise en compte des notes automatiques et manuelles";
    case "manual_total":
      return "Décision finale laissée au porteur de projet";
    default:
      return "Système de notation configuré";
  }
};

const getScoringDecisionLabel = (
  decision: string | null | undefined,
  decisionMode: string | null | undefined,
): string => {
  switch (decision) {
    case "auto_accepted":
      return "Projet validé automatiquement";
    case "auto_rejected":
      return "Projet rejeté automatiquement";
    case "manual_scoring_required":
      return "En attente de l'évaluation du porteur de projet";
    case "manual_review_required":
      return decisionMode === "manual_total"
        ? "En attente de l'évaluation du porteur de projet"
        : "En attente de votre validation";
    default:
      return "Évaluation en cours";
  }
};

const toLocalDateOnly = (date = new Date()): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const normalizeDateOnly = (raw: unknown): string | null => {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const direct = raw.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return toLocalDateOnly(parsed);
};

const formatDateForDisplay = (raw: unknown): string | null => {
  const normalized = normalizeDateOnly(raw);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  return `${day}/${month}/${year}`;
};

const SearchableSelect = ({
  value,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  loadingLabel,
  onChange,
}: {
  value?: string;
  options: SelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  disabled?: boolean;
  loadingLabel?: string;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);

  const selected = options.find((option) => option.value === (value ?? ""));
  const buttonLabel =
    disabled && !selected && loadingLabel ? loadingLabel : selected?.label ?? placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-10 w-full justify-between rounded-md border border-input bg-background px-3 py-2 text-sm font-normal hover:bg-white hover:text-black"
        >
          <span className="truncate text-left">{buttonLabel}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

type CallForProjectsCreateProjectProps = {
  listPath?: string;
  projectId?: number | null;
  hardRedirectOnSubmit?: boolean;
};

const CallForProjectsCreateProject = ({
  listPath = "/submit/appels-a-projets",
  projectId = null,
  hardRedirectOnSubmit = false,
}: CallForProjectsCreateProjectProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { postData, loading } = usePostData<any>();
  const validationMutation = usePostData<any>();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(1);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [projectStatus, setProjectStatus] = useState<number | string | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [initializedDraftId, setInitializedDraftId] = useState<number | null>(null);
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const dirtyFieldsRef = useRef(dirtyFields);
  const pendingFieldSavesRef = useRef(new Map<string, Promise<boolean>>());
  const [programDirty, setProgramDirty] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [fundingTab, setFundingTab] = useState("obtainedFunding");
  const [customGuidedFundingHasLocalValue, setCustomGuidedFundingHasLocalValue] = useState(false);
  const [projectFundingCurrencySelection, setProjectFundingCurrencySelection] = useState<string | null>(null);
  const [openResetModal, setOpenResetModal] = useState(false);
  const [isResettingDraft, setIsResettingDraft] = useState(false);
  const [openValidate, setOpenValidate] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const [openValidateUser, setOpenValidateUser] = useState(false);
  const [openResetInProgress, setOpenResetInProgress] = useState(false);
  const [loadingValidate, setLoadingValidate] = useState(false);
  const [loadingValidateUser, setLoadingValidateUser] = useState(false);
  const [loadingResetInProgress, setLoadingResetInProgress] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExportingProject, setIsExportingProject] = useState(false);
  const [creatorValidationSuccess, setCreatorValidationSuccess] = useState(false);
  const [validateUserFromProjectModal, setValidateUserFromProjectModal] = useState(false);
  const [manualScoreValues, setManualScoreValues] = useState<Record<string, string>>({});
  const [manualScoreErrors, setManualScoreErrors] = useState<Record<string, string>>({});
  const [savingManualScores, setSavingManualScores] = useState<Record<string, boolean>>({});
  const [isCompletingEvaluation, setIsCompletingEvaluation] = useState(false);
  const [isEvaluationMode, setIsEvaluationMode] = useState(false);
  const [openIncompleteEvaluationModal, setOpenIncompleteEvaluationModal] = useState(false);
  const [liveScoringSummary, setLiveScoringSummary] = useState<DraftCurrentResponse["scoringSummary"]>(null);
  const [liveScoringDecision, setLiveScoringDecision] = useState<DraftCurrentResponse["scoringDecision"]>(null);
  const [liveScoringSettings, setLiveScoringSettings] = useState<DraftCurrentResponse["scoringSettings"]>(null);
  const [manualEvaluationCompleted, setManualEvaluationCompleted] = useState(false);
  const isApplyingUrlStepRef = useRef(false);
  const lastAppliedUrlStepRef = useRef<number | null>(null);
  const lastStableStepDataRef = useRef<ApiStep | null>(null);

  useEffect(() => {
    dirtyFieldsRef.current = dirtyFields;
  }, [dirtyFields]);

  const trackFieldSave = useCallback((fieldKey: string, promise: Promise<boolean>) => {
    pendingFieldSavesRef.current.set(fieldKey, promise);
    promise.finally(() => {
      if (pendingFieldSavesRef.current.get(fieldKey) === promise) {
        pendingFieldSavesRef.current.delete(fieldKey);
      }
    });
    return promise;
  }, []);

  const waitForPendingFieldSaves = useCallback(async (fieldKeys: string[]) => {
    const pendingSaves = fieldKeys
      .map((fieldKey) => pendingFieldSavesRef.current.get(fieldKey))
      .filter((promise): promise is Promise<boolean> => Boolean(promise));

    if (pendingSaves.length === 0) return true;

    const results = await Promise.allSettled(pendingSaves);
    return results.every(
      (result) => result.status === "fulfilled" && result.value !== false,
    );
  }, []);

  const isEditMode = Number.isFinite(Number(projectId)) && Number(projectId) > 0;
  const isSubmitContext = useMemo(
    () => location.pathname.startsWith("/submit"),
    [location.pathname],
  );
  const shouldSyncStepInUrl = useMemo(
    () => isSubmitContext,
    [isSubmitContext],
  );
  const returnToListPath = useCallback(() => {
    if (hardRedirectOnSubmit && isSubmitContext) {
      window.location.assign(listPath);
      return;
    }

    navigate(listPath, {
      state: {
        refreshProgramCaches: true,
      },
    });
  }, [hardRedirectOnSubmit, isSubmitContext, listPath, navigate]);
  const programIdFromUrl = useMemo(() => {
    const raw = searchParams.get("programId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const draftProgramId = useMemo(() => {
    if (isEditMode) return null;
    return programIdFromUrl ?? selectedProgramId ?? null;
  }, [isEditMode, programIdFromUrl, selectedProgramId]);
  const projectTypeIdFromUrl = useMemo(() => {
    const raw = searchParams.get("projectTypeId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const draftEndpoint = isEditMode
    ? `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/project/${projectId}`
    : `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/current${(() => {
      const params = new URLSearchParams();
      if (draftProgramId) params.set("programId", `${draftProgramId}`);
      if (projectTypeIdFromUrl) params.set("projectTypeId", `${projectTypeIdFromUrl}`);
      const query = params.toString();
      return query ? `?${query}` : "";
    })()}`;

  const draft = useFetchData<DraftCurrentResponse>(draftEndpoint);
  const projectDetailsEndpoint =
    isEditMode && projectId
      ? `${import.meta.env.VITE_API_ENDPOINT}/project/${projectId}?excludes=indicator,reporting`
      : "";
  const projectDetails = useFetchDataV2<any>(projectDetailsEndpoint, {
    noCache: true,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });
  const sidebar = useFetchData<SidebarData>(
    `${import.meta.env.VITE_API_ENDPOINT}/call-for-project/submit/sidebar`,
  );
  const programsInfo = useFetchData<ProgramInfo[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
  );
  const projectTypes = useFetchData<ProjectType[]>(`${import.meta.env.VITE_API_ENDPOINT}/project-types`);
  const countries = useFetchData<Country[]>(`${import.meta.env.VITE_API_ENDPOINT}/countries`);
  const devises = useFetchData<Devise[]>(`${import.meta.env.VITE_API_ENDPOINT}/devises`);

  const availablePrograms = useMemo(
    () => (Array.isArray(sidebar.data?.availablePrograms) ? sidebar.data.availablePrograms : []),
    [sidebar.data],
  );
  const allPrograms = useMemo(
    () => asArray<ProgramInfo>(programsInfo.data),
    [programsInfo.data],
  );
  const stepFromUrl = useMemo(() => {
    const raw = searchParams.get("step");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const programNameFromUrl = useMemo(() => {
    const raw = searchParams.get("programName");
    return raw && raw.trim().length > 0 ? raw.trim() : null;
  }, [searchParams]);

  useEffect(() => {
    if (!draft.data?.draftId) return;

    setDraftId(draft.data.draftId);

    if (initializedDraftId === draft.data.draftId) {
      return;
    }

    const incomingValues = draft.data.answers && typeof draft.data.answers === "object"
      ? draft.data.answers
      : {};

    setValues(incomingValues);
    setSelectedProgramId(draft.data.programId ?? null);
    setProjectStatus(draft.data.status ?? null);
    setLiveScoringSummary(draft.data.scoringSummary ?? null);
    setLiveScoringDecision(draft.data.scoringDecision ?? null);
    setLiveScoringSettings(draft.data.scoringSettings ?? null);
    setManualEvaluationCompleted(Boolean(draft.data.manualEvaluationCompleted));
    setInitializedDraftId(draft.data.draftId);
    setDirtyFields(new Set());
    setProgramDirty(false);
    const orderedSteps = normalizeSchemaSteps(draft.data.schema ?? []);
    if (isEditMode) {
      if (!isSubmitContext) {
        setMaxUnlockedStep(orderedSteps[orderedSteps.length - 1]?.step ?? 1);
        return;
      }
      const numericStatus = Number(draft.data.status);
      const shouldKeepStepLock = Number.isFinite(numericStatus) && numericStatus <= 1;
      if (shouldKeepStepLock) {
        const isFieldMissingRequiredValue = (field: ApiField, value: any) => {
          const normalizedType = normalize(field.type ?? "");
          if (isSearchFundingNativeField(field)) {
            return value !== true && value !== false;
          }
          if (normalizedType === "map") {
            const mapValue = toMapFieldValue(value);
            const count = mapValue.drawn.polygons.length > 0
              ? mapValue.drawn.polygons.length
              : mapValue.parcel.length + mapValue.location.length;
            return count === 0;
          }
          if (normalizedType === "checkbox") {
            return !Array.isArray(value) || value.length === 0;
          }
          if (normalizedType === "upload") {
            const uploads = toUploadMedias(value);
            return uploads.length === 0;
          }
          if (normalizedType === "funding") {
            return !hasAnyFundingValue(value);
          }
          if (normalizedType === "plantedspecies" || normalizedType === "protectedspecies") {
            const speciesItems = toSpeciesItems(value);
            return speciesItems.length === 0;
          }
          return value === undefined || value === null || `${value}`.trim() === "";
        };

        let unlockedStep = orderedSteps[0]?.step ?? 1;
        for (const step of orderedSteps) {
          const stepFields = [
            ...(Array.isArray(step.fields) ? step.fields : []),
            ...((Array.isArray(step.sections) ? step.sections : []).flatMap((section) =>
              Array.isArray(section.fields) ? section.fields : []
            )),
          ];
          const hasMissingRequired = stepFields.some((field) => {
            const nativeKey = normalize(field.nativeField ?? field.name ?? "");
            const candidateCallForm =
              Array.isArray(user?.roles) &&
              user.roles.includes("ROLE_CPINTERVENANT") &&
              location.pathname.startsWith("/submit/");
            if (
              candidateCallForm &&
              HIDDEN_CALL_CANDIDATE_DESCRIPTIF_KEYS.has(nativeKey)
            ) {
              return false;
            }
            if (!field.required && !isSearchFundingNativeField(field)) return false;
            return isFieldMissingRequiredValue(field, incomingValues[`${field.id}`]);
          });
          if (hasMissingRequired) {
            break;
          }
          unlockedStep = Math.min(
            step.step + 1,
            orderedSteps[orderedSteps.length - 1]?.step ?? step.step,
          );
        }
        setMaxUnlockedStep(unlockedStep);
      } else {
        setMaxUnlockedStep(orderedSteps[orderedSteps.length - 1]?.step ?? 1);
      }
    } else {
      setMaxUnlockedStep(orderedSteps[0]?.step ?? 1);
    }
  }, [draft.data, initializedDraftId, isEditMode, isSubmitContext]);

useEffect(() => {
    if (isEditMode) return;
    if (programIdFromUrl) return;
    if (selectedProgramId === null && availablePrograms.length > 0) {
      setSelectedProgramId(availablePrograms[0].id);
      setProgramDirty(true);
    }
  }, [availablePrograms, isEditMode, programIdFromUrl, selectedProgramId]);

  useEffect(() => {
    if (isEditMode) return;
    if (!programIdFromUrl) return;
    if (Number(selectedProgramId) !== Number(programIdFromUrl)) {
      setSelectedProgramId(Number(programIdFromUrl));
      setProgramDirty(true);
    }
  }, [isEditMode, programIdFromUrl, selectedProgramId]);

  const selectedProgram = useMemo(
    () => availablePrograms.find((program) => Number(program.id) === Number(selectedProgramId)) ?? null,
    [availablePrograms, selectedProgramId],
  );
  const lockedSearchFundingFromProgram = useMemo(() => {
    if (!selectedProgram) return null;
    return typeof selectedProgram.chercheurFond === "boolean"
      ? selectedProgram.chercheurFond
      : null;
  }, [selectedProgram]);
  const selectedProgramFromAll = useMemo(
    () => allPrograms.find((program) => Number(program.id) === Number(selectedProgramId)) ?? null,
    [allPrograms, selectedProgramId],
  );
  const isCallExpired = useMemo(() => {
    const endDate =
      normalizeDateOnly(selectedProgram?.dateFin)
      ?? normalizeDateOnly(selectedProgramFromAll?.dateFin);
    if (!endDate) return false;
    return toLocalDateOnly() > endDate;
  }, [selectedProgram?.dateFin, selectedProgramFromAll?.dateFin]);
  const displayedCallDeadline = useMemo(
    () => formatDateForDisplay(selectedProgram?.dateFin ?? selectedProgramFromAll?.dateFin),
    [selectedProgram?.dateFin, selectedProgramFromAll?.dateFin],
  );

  const projectTypesList = useMemo(() => asArray<ProjectType>(projectTypes.data), [projectTypes.data]);
  const countriesList = useMemo(() => asArray<Country>(countries.data), [countries.data]);
  const devisesList = useMemo(() => asArray<Devise>(devises.data), [devises.data]);

  const projectTypeOptions = useMemo<SelectOption[]>(
    () =>
      projectTypesList.map((option) => ({
        value: `${option.id}`,
        label: option.name || option.projectTypeLangs?.[0]?.name || `Type #${option.id}`,
      })),
    [projectTypesList],
  );

  const countryOptions = useMemo<SelectOption[]>(
    () =>
      countriesList.map((option) => ({
        value: `${option.id}`,
        label: option.name,
      })),
    [countriesList],
  );

  const deviseOptions = useMemo<SelectOption[]>(
    () =>
      devisesList.map((option) => ({
        value: `${option.id}`,
        label: option.name
          ? `${option.name}${option.symbol ? ` (${option.symbol})` : ""}`
          : option.code
            ? `${option.code}${option.symbol ? ` (${option.symbol})` : ""}`
            : `Devise #${option.id}`,
      })),
    [devisesList],
  );

  const schemaSteps = useMemo(() => {
    const source = Array.isArray(draft.data?.schema) ? draft.data.schema : [];
    return normalizeSchemaSteps(source);
  }, [draft.data]);
  const autoProjectTypeId = useMemo(
    () => extractAutoProjectTypeIdFromSchema(schemaSteps),
    [schemaSteps],
  );
  const isCpIntervenant = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes("ROLE_CPINTERVENANT"),
    [user?.roles],
  );
  const isSubmitCallFlow = useMemo(
    () => location.pathname.startsWith("/submit/"),
    [location.pathname],
  );
  const isHiddenCallCandidateField = useCallback(
    (field: ApiField) =>
      isCpIntervenant &&
      isSubmitCallFlow &&
      HIDDEN_CALL_CANDIDATE_DESCRIPTIF_KEYS.has(
        normalize(field.nativeField ?? field.name ?? ""),
      ),
    [isCpIntervenant, isSubmitCallFlow],
  );
  const lockedProjectTypeId = useMemo(() => {
    if (autoProjectTypeId) return autoProjectTypeId;
    if (projectTypeIdFromUrl) return projectTypeIdFromUrl;
    return null;
  }, [autoProjectTypeId, projectTypeIdFromUrl]);
  const allSchemaFields = useMemo(
    () =>
      schemaSteps.flatMap((step) => [
        ...(Array.isArray(step.fields) ? step.fields : []),
        ...((Array.isArray(step.sections) ? step.sections : []).flatMap((section) =>
          Array.isArray(section.fields) ? section.fields : [],
        )),
      ]),
    [schemaSteps],
  );
  const fieldById = useMemo(() => {
    const map = new Map<number, ApiField>();
    allSchemaFields.forEach((field) => {
      map.set(Number(field.id), field);
    });
    return map;
  }, [allSchemaFields]);
  const projectNameFieldId = useMemo(() => {
    const match = allSchemaFields.find((field) => {
      const nativeKey = normalize(field.nativeField ?? field.name ?? "");
      return nativeKey === "name";
    });
    return match?.id ? `${match.id}` : null;
  }, [allSchemaFields]);
  const projectTypeFieldId = useMemo(() => {
    const match = allSchemaFields.find(isProjectTypeField);
    return match?.id ? `${match.id}` : null;
  }, [allSchemaFields]);
  const currentProjectName = useMemo(() => {
    const candidates = [
      projectNameFieldId ? values[projectNameFieldId] : null,
      projectDetails.data?.name,
      draft.data?.projectName,
      draft.data?.name,
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null) continue;
      const normalized = `${candidate}`.trim();
      if (normalized.length > 0) return normalized;
    }

    return "";
  }, [draft.data?.name, draft.data?.projectName, projectDetails.data?.name, projectNameFieldId, values]);
  const resolvedProjectDisplayName = useMemo(() => {
    if (currentProjectName) return currentProjectName;
    if (projectId) return `Projet #${projectId}`;
    return "Fiche projet";
  }, [currentProjectName, projectId]);
  const currentProjectDetail = useMemo(
    () => (isEditMode ? projectDetails.data ?? null : null),
    [isEditMode, projectDetails.data],
  );
  const syncProjectStatus = useCallback(
    (nextStatusValue: unknown, _payload: any = {}) => {
      if (nextStatusValue === undefined || nextStatusValue === null) return;

      const nextStatus = Number(nextStatusValue);
      if (!Number.isFinite(nextStatus)) return;

      setProjectStatus(nextStatus);
    },
    [],
  );

  useEffect(() => {
    setCustomGuidedFundingHasLocalValue(false);
    setProjectFundingCurrencySelection(null);
  }, [projectId]);
  const projectFundingDisplayState = useMemo(
    () => buildFundingDisplayState(currentProjectDetail),
    [currentProjectDetail],
  );
  const displayedProjectObtainedFundings = projectFundingDisplayState.displayedObtainedFunding;
  const displayedProjectDesiredFundings = projectFundingDisplayState.displayedDesiredFunding;
  const hasProjectFundingValue = useMemo(
    () =>
      [...displayedProjectObtainedFundings, ...displayedProjectDesiredFundings].some((funding: any) => {
        const title = String(
          funding?.title ?? funding?.name ?? funding?.label ?? "",
        ).trim();
        const amount = Number(funding?.convertedAmount ?? funding?.amount ?? 0);
        return title.length > 0 || (Number.isFinite(amount) && amount > 0);
      }),
    [displayedProjectDesiredFundings, displayedProjectObtainedFundings],
  );
  const shouldSkipLegacyFundingDraftSave = useCallback(
    (fieldKey: string) => {
      if (!isEditMode || !projectId) return false;
      const field = fieldById.get(Number(fieldKey));
      const type = normalize(field?.type ?? "");
      const native = normalize(field?.nativeField ?? field?.name ?? "");
      return (
        type === "funding" ||
        native === "desiredfunding" ||
        native === "obtainedfunding"
      );
    },
    [fieldById, isEditMode, projectId],
  );
  const projectFundingTotal = useMemo(
    () =>
      [...displayedProjectObtainedFundings, ...displayedProjectDesiredFundings].reduce(
        (sum, funding: any) => {
          const amount = Number(funding?.convertedAmount ?? funding?.amount ?? 0);
          return Number.isFinite(amount) ? sum + amount : sum;
        },
        0,
      ),
    [displayedProjectDesiredFundings, displayedProjectObtainedFundings],
  );
  const projectFundingLocalCurrency = useMemo(
    () =>
      String(
        currentProjectDetail?.country?.currency?.code ||
          currentProjectDetail?.country?.currencyCode ||
          "EUR",
      )
        .trim()
        .toUpperCase(),
    [currentProjectDetail],
  );
  const projectFundingCurrency = useMemo(() => {
    const selectedCurrency = String(projectFundingCurrencySelection || "")
      .trim()
      .toUpperCase();

    if (selectedCurrency === "EUR") return "EUR";
    if (
      selectedCurrency &&
      selectedCurrency === projectFundingLocalCurrency
    ) {
      return selectedCurrency;
    }

    return projectFundingLocalCurrency;
  }, [projectFundingCurrencySelection, projectFundingLocalCurrency]);
  const projectFundingCurrencySymbol = useMemo(
    () => {
      if (projectFundingCurrency === "EUR") return "€";

      return (
        currentProjectDetail?.country?.currency?.symbol ||
        currentProjectDetail?.country?.currency?.symbolNative ||
        projectFundingCurrency
      );
    },
    [currentProjectDetail, projectFundingCurrency],
  );
  const scoringSummary = isEditMode ? liveScoringSummary : null;
  const scoringDecision = isEditMode ? liveScoringDecision : null;
  const scoringSettings = isEditMode ? liveScoringSettings : null;
  const isScoringEnabled = useMemo(
    () => Boolean(scoringSettings?.enabled),
    [scoringSettings?.enabled],
  );
  const resolvedProjectStatus = useMemo(() => {
    const localStatus = Number(projectStatus);
    const remoteStatus = Number(currentProjectDetail?.status);

    if (
      localStatus === projectStatusEnum.Rejected
      || remoteStatus === projectStatusEnum.Rejected
    ) {
      return projectStatusEnum.Rejected;
    }

    if (
      localStatus === projectStatusEnum.Validated
      || remoteStatus === projectStatusEnum.Validated
    ) {
      return projectStatusEnum.Validated;
    }

    if (
      localStatus === projectStatusEnum.AwaitingValidationPP
      || remoteStatus === projectStatusEnum.AwaitingValidationPP
    ) {
      return projectStatusEnum.AwaitingValidationPP;
    }
    if (Number.isFinite(localStatus)) return localStatus;
    if (Number.isFinite(remoteStatus)) return remoteStatus;
    return null;
  }, [currentProjectDetail?.status, projectStatus]);
  const projectStatusBadge = useMemo(
    () => getProjectStatusBadge(resolvedProjectStatus, { submitContext: isSubmitContext }),
    [isSubmitContext, resolvedProjectStatus],
  );
  const isCarrierUser = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes("ROLE_CARRIER"),
    [user?.roles],
  );
  const scoringDisplayPercentage = useMemo(() => {
    if (!scoringSummary) return null;
    return scoringSummary.finalPercentage
      ?? scoringSummary.currentPercentage
      ?? scoringSummary.autoPercentage
      ?? null;
  }, [scoringSummary]);
  const scoringTheme = useMemo(
    () => getScoringDecisionTheme(scoringDecision?.decision ?? null, resolvedProjectStatus),
    [resolvedProjectStatus, scoringDecision?.decision],
  );
  const scoringDecisionMode = useMemo(
    () => (isScoringEnabled ? (scoringDecision?.decisionMode ?? scoringSettings?.decisionMode ?? null) : null),
    [isScoringEnabled, scoringDecision?.decisionMode, scoringSettings?.decisionMode],
  );
  const isAutoThresholdDecisionMode = useMemo(
    () =>
      scoringDecisionMode === "auto_threshold"
      || scoringDecisionMode === "auto_total",
    [scoringDecisionMode],
  );
  const showPerFieldScores = useMemo(
    () =>
      isEditMode
      && !isSubmitContext
      && isCarrierUser
      && isScoringEnabled
      && [projectStatusEnum.AwaitingValidation, projectStatusEnum.AwaitingValidationPP, projectStatusEnum.Rejected]
        .includes(Number(resolvedProjectStatus)),
    [isCarrierUser, isEditMode, isScoringEnabled, isSubmitContext, resolvedProjectStatus],
  );
  const shouldShowDashboardScoringHeader = useMemo(
    () =>
      isEditMode
      && !isSubmitContext
      && isCarrierUser
      && isScoringEnabled
      && Boolean(scoringSummary)
      && Number(resolvedProjectStatus) >= projectStatusEnum.AwaitingValidation,
    [
      isCarrierUser,
      isEditMode,
      isScoringEnabled,
      isSubmitContext,
      resolvedProjectStatus,
      scoringSummary,
    ],
  );
  const handleProjectExport = useCallback(
    async (
      format: "xlsx" | "docx",
      modules: CandidateProjectExportModule[],
    ) => {
      const numericProjectId = Number(projectId);
      if (!Number.isFinite(numericProjectId) || numericProjectId <= 0) return;
      if (modules.length === 0) return;

      try {
        setIsExportingProject(true);
        await downloadCandidateProjectExport(numericProjectId, format, modules);
        setIsExportDialogOpen(false);
      } catch (error) {
        toast({
          title: "Export impossible",
          description:
            error instanceof Error
              ? error.message
              : "Une erreur est survenue pendant la génération du fichier.",
          variant: "destructive",
        });
      } finally {
        setIsExportingProject(false);
      }
    },
    [projectId, toast],
  );
  const scoringBreakdownByFieldId = useMemo(() => {
    const entries = Array.isArray(scoringSummary?.breakdown) ? scoringSummary.breakdown : [];
    const next = new Map<number, (typeof entries)[number]>();
    entries.forEach((entry) => {
      const fieldId = Number(entry?.fieldId);
      if (Number.isFinite(fieldId) && fieldId > 0) {
        next.set(fieldId, entry);
      }
    });
    return next;
  }, [scoringSummary?.breakdown]);
  const manualScoringEntries = useMemo(
    () =>
      Array.from(scoringBreakdownByFieldId.entries()).filter(([, entry]) => {
        if (entry?.mode !== "manual" || !entry?.includedInPercentage) {
          return false;
        }

        return Boolean(entry?.pendingReview) || entry?.points !== null && entry?.points !== undefined;
      }),
    [scoringBreakdownByFieldId],
  );
  const hasManualScoringWork = useMemo(
    () =>
      manualScoringEntries.length > 0
      || Number(scoringSummary?.manualIncludedCount ?? 0) > 0,
    [manualScoringEntries.length, scoringSummary?.manualIncludedCount],
  );
  const canEvaluateManually = useMemo(
    () =>
      showPerFieldScores
      && (isAutoThresholdDecisionMode || scoringDecisionMode === "manual_total")
      && hasManualScoringWork
      && Number(resolvedProjectStatus) === projectStatusEnum.AwaitingValidationPP,
    [hasManualScoringWork, isAutoThresholdDecisionMode, resolvedProjectStatus, scoringDecisionMode, showPerFieldScores],
  );
  const localManualReviewedCount = useMemo(
    () =>
      manualScoringEntries.filter(([fieldId]) => {
        const value = manualScoreValues[String(fieldId)];
        if (value === undefined || value === null) return false;
        if (`${value}`.trim() === "") return false;
        return Number.isFinite(Number(value));
      }).length,
    [manualScoreValues, manualScoringEntries],
  );
  const localManualPendingCount = useMemo(
    () => Math.max(manualScoringEntries.length - localManualReviewedCount, 0),
    [localManualReviewedCount, manualScoringEntries.length],
  );
  const effectiveManualPendingCount = useMemo(
    () => (canEvaluateManually && isEvaluationMode ? localManualPendingCount : Number(scoringSummary?.manualPendingCount ?? 0)),
    [canEvaluateManually, isEvaluationMode, localManualPendingCount, scoringSummary?.manualPendingCount],
  );
  const effectiveManualReviewedCount = useMemo(
    () => (canEvaluateManually && isEvaluationMode ? localManualReviewedCount : Number(scoringSummary?.manualReviewedCount ?? 0)),
    [canEvaluateManually, isEvaluationMode, localManualReviewedCount, scoringSummary?.manualReviewedCount],
  );
  const effectiveManualIncludedCount = useMemo(
    () => (canEvaluateManually && isEvaluationMode ? manualScoringEntries.length : Number(scoringSummary?.manualIncludedCount ?? 0)),
    [canEvaluateManually, isEvaluationMode, manualScoringEntries.length, scoringSummary?.manualIncludedCount],
  );
  const manualScoreMax = Number(scoringSummary?.manualReviewedMaxScore ?? 0)
    + Number(scoringSummary?.manualPendingMaxScore ?? 0);
  const canCompleteEvaluation = useMemo(
    () =>
      canEvaluateManually &&
      isEvaluationMode &&
      (isAutoThresholdDecisionMode || scoringDecisionMode === "manual_total") &&
      effectiveManualPendingCount === 0,
    [canEvaluateManually, effectiveManualPendingCount, isAutoThresholdDecisionMode, isEvaluationMode, scoringDecisionMode],
  );
  const shouldShowCarrierDecisionButtons = useMemo(() => {
    if (!(isEditMode && isCarrierUser && Number(resolvedProjectStatus) === projectStatusEnum.AwaitingValidationPP)) {
      return false;
    }

    if (scoringDecisionMode === "manual_total") {
      if (effectiveManualIncludedCount === 0) {
        return true;
      }

      return !isEvaluationMode
        && manualEvaluationCompleted
        && effectiveManualPendingCount === 0;
    }

    if (!isAutoThresholdDecisionMode) {
      return true;
    }

    if (effectiveManualIncludedCount === 0) {
      return scoringDecision?.decision === "manual_review_required";
    }

    return !isEvaluationMode
      && manualEvaluationCompleted
      && scoringDecision?.decision === "manual_review_required"
      && effectiveManualPendingCount === 0;
  }, [
    isEvaluationMode,
    isCarrierUser,
    isEditMode,
    effectiveManualIncludedCount,
    manualEvaluationCompleted,
    effectiveManualPendingCount,
    resolvedProjectStatus,
    scoringDecision?.decision,
    scoringDecisionMode,
    isAutoThresholdDecisionMode,
  ]);
  const shouldShowPerFieldScoreDetails = useMemo(() => {
    if (!showPerFieldScores) {
      return false;
    }

    if (
      (isAutoThresholdDecisionMode || scoringDecisionMode === "manual_total")
      && effectiveManualIncludedCount > 0
      && Number(resolvedProjectStatus) === projectStatusEnum.AwaitingValidationPP
      && !isEvaluationMode
    ) {
      return false;
    }

    return true;
  }, [effectiveManualIncludedCount, isAutoThresholdDecisionMode, isEvaluationMode, resolvedProjectStatus, scoringDecisionMode, showPerFieldScores]);
  const hasStartedOrCompletedEvaluation = useMemo(
    () =>
      canEvaluateManually
      && (
        isEvaluationMode
        || (
          effectiveManualReviewedCount > 0
          && !manualEvaluationCompleted
        )
        || (
          (isAutoThresholdDecisionMode || scoringDecisionMode === "manual_total")
          && manualEvaluationCompleted
        )
      ),
    [
      canEvaluateManually,
      effectiveManualPendingCount,
      effectiveManualReviewedCount,
      isAutoThresholdDecisionMode,
      isEvaluationMode,
      manualEvaluationCompleted,
      scoringDecisionMode,
    ],
  );
  const shouldShowFinishEvaluationAction = useMemo(
    () =>
      canEvaluateManually
      && (isAutoThresholdDecisionMode || scoringDecisionMode === "manual_total")
      && !manualEvaluationCompleted
      && (isEvaluationMode || effectiveManualReviewedCount > 0),
    [
      canEvaluateManually,
      effectiveManualReviewedCount,
      isAutoThresholdDecisionMode,
      isEvaluationMode,
      manualEvaluationCompleted,
      scoringDecisionMode,
    ],
  );
  const shouldShowManualScoringInputs = useMemo(
    () =>
      canEvaluateManually
      && !manualEvaluationCompleted
      && (isEvaluationMode || effectiveManualReviewedCount > 0),
    [canEvaluateManually, effectiveManualReviewedCount, isEvaluationMode, manualEvaluationCompleted],
  );
  const shouldHideProjectFormUntilEvaluationStarts = useMemo(
    () =>
      canEvaluateManually
      && (isAutoThresholdDecisionMode || scoringDecisionMode === "manual_total")
      && effectiveManualIncludedCount > 0
      && !hasStartedOrCompletedEvaluation,
    [canEvaluateManually, effectiveManualIncludedCount, hasStartedOrCompletedEvaluation, isAutoThresholdDecisionMode, scoringDecisionMode],
  );
  useEffect(() => {
    if (!showPerFieldScores) return;
    const nextValues: Record<string, string> = {};
    const nextErrors: Record<string, string> = {};
    scoringBreakdownByFieldId.forEach((entry, fieldId) => {
      if (entry?.mode !== "manual") return;
      nextValues[String(fieldId)] =
        entry?.points === null || entry?.points === undefined ? "" : String(entry.points);
      nextErrors[String(fieldId)] = "";
    });
    setManualScoreValues(nextValues);
    setManualScoreErrors(nextErrors);
  }, [scoringBreakdownByFieldId, showPerFieldScores]);
  useEffect(() => {
    if (!canEvaluateManually) {
      setIsEvaluationMode(false);
    }
  }, [canEvaluateManually, projectId]);
  const isFormReadOnlyByCallDate = useMemo(
    () => isSubmitContext && isCallExpired,
    [isCallExpired, isSubmitContext],
  );
  const isRejectedReadOnlyInSubmit = useMemo(
    () => isSubmitContext && Number(resolvedProjectStatus) === projectStatusEnum.Rejected,
    [isSubmitContext, resolvedProjectStatus],
  );
  const areProjectAnswersReadOnly = useMemo(
    () => isFormReadOnlyByCallDate || showPerFieldScores || isRejectedReadOnlyInSubmit,
    [isFormReadOnlyByCallDate, isRejectedReadOnlyInSubmit, showPerFieldScores],
  );
  const isProjectCreatorCpIntervenant = useMemo(
    () =>
      Array.isArray(currentProjectDetail?.creator?.roles)
      && currentProjectDetail.creator.roles.includes("ROLE_CPINTERVENANT"),
    [currentProjectDetail?.creator?.roles],
  );
  const canValidateCandidate = useMemo(
    () =>
      Boolean(currentProjectDetail?.searchFinancingInternal)
      && isProjectCreatorCpIntervenant
      && !creatorValidationSuccess,
    [creatorValidationSuccess, currentProjectDetail?.searchFinancingInternal, isProjectCreatorCpIntervenant],
  );
  const canResetCandidateProjectToInProgress = useMemo(
    () =>
      isEditMode
      && isCarrierUser
      && isProjectCreatorCpIntervenant
      && Number(resolvedProjectStatus) > projectStatusEnum.InProgress
      && Number(resolvedProjectStatus) !== projectStatusEnum.Validated,
    [isCarrierUser, isEditMode, isProjectCreatorCpIntervenant, resolvedProjectStatus],
  );
  const isEditableDraftProject = useMemo(() => {
    const numericStatus = Number(resolvedProjectStatus);
    return isEditMode && Number.isFinite(numericStatus) && numericStatus <= 1;
  }, [isEditMode, resolvedProjectStatus]);
  const countryFieldIds = useMemo(
    () =>
      allSchemaFields
        .filter(
          (field) =>
            field.type === "country" || normalize(field.nativeField ?? field.name ?? "") === "country",
        )
        .map((field) => `${field.id}`),
    [allSchemaFields],
  );
  const searchFundingFieldIds = useMemo(
    () =>
      allSchemaFields
        .filter((field) => isSearchFundingNativeField(field))
        .map((field) => `${field.id}`),
    [allSchemaFields],
  );
  const selectedCountryId = useMemo(() => {
    for (const fieldId of countryFieldIds) {
      const raw = values[fieldId];
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  }, [countryFieldIds, values]);
  const defaultFundingDeviseId = useMemo(() => {
    const selectedCountryCurrencyId =
      selectedCountryId !== null
        ? Number(
          countriesList.find((country) => Number(country.id) === Number(selectedCountryId))?.currency?.id ?? 0,
        ) || null
        : null;
    if (selectedCountryCurrencyId) return `${selectedCountryCurrencyId}`;

    const eur = devisesList.find((devise) => `${(devise.code ?? "").toUpperCase()}` === "EUR");
    if (eur?.id) return `${eur.id}`;

    return "";
  }, [countriesList, devisesList, selectedCountryId]);

  const projectTypeFieldIds = useMemo(
    () =>
      allSchemaFields
        .filter((field) => isProjectTypeField(field))
        .map((field) => `${field.id}`),
    [allSchemaFields],
  );

  const selectedProjectTypeId = useMemo(() => {
    for (const fieldId of projectTypeFieldIds) {
      const raw = values[fieldId];
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    const projectTypeFromProject = Number(currentProjectDetail?.type?.id ?? 0);
    if (Number.isFinite(projectTypeFromProject) && projectTypeFromProject > 0) {
      return projectTypeFromProject;
    }
    return lockedProjectTypeId ?? null;
  }, [currentProjectDetail?.type?.id, lockedProjectTypeId, projectTypeFieldIds, values]);

  useEffect(() => {
    if (isFormReadOnlyByCallDate) return;
    if (!lockedProjectTypeId || projectTypeFieldIds.length === 0) return;

    const autoValue = `${lockedProjectTypeId}`;
    const fieldsToUpdate = projectTypeFieldIds.filter(
      (fieldId) => `${values[fieldId] ?? ""}` !== autoValue,
    );
    if (fieldsToUpdate.length === 0) return;

    setValues((prev) => {
      const next = { ...prev };
      fieldsToUpdate.forEach((fieldId) => {
        next[fieldId] = autoValue;
      });
      return next;
    });

    setDirtyFields((prev) => {
      const next = new Set(prev);
      fieldsToUpdate.forEach((fieldId) => next.add(fieldId));
      return next;
    });
  }, [isFormReadOnlyByCallDate, lockedProjectTypeId, projectTypeFieldIds, values]);

  const steps = useMemo(
    () => filterSchemaByProjectType(schemaSteps, selectedProjectTypeId),
    [schemaSteps, selectedProjectTypeId],
  );

  const currentStepData = steps.find((s) => s.step === currentStep);
  useEffect(() => {
    if (currentStepData) {
      lastStableStepDataRef.current = currentStepData;
    }
  }, [currentStepData]);
  const effectiveCurrentStepData = currentStepData ?? lastStableStepDataRef.current;
  const currentStepDescription = useMemo(
    () => effectiveCurrentStepData?.description ?? effectiveCurrentStepData?.stepDescription ?? "",
    [effectiveCurrentStepData],
  );
  const isLastStep = currentStep === (steps[steps.length - 1]?.step ?? 1);
  const isLoading = draft.loading;

  useEffect(() => {
    if (!isSubmitContext) {
      setMaxUnlockedStep(steps[steps.length - 1]?.step ?? 1);
      if (steps.length > 0 && !steps.some((step) => step.step === currentStep)) {
        setCurrentStep(steps[0].step);
      }
      return;
    }
    if (isFormReadOnlyByCallDate) {
      setMaxUnlockedStep(steps[steps.length - 1]?.step ?? 1);
      return;
    }
    if (steps.length > 0 && !steps.some((step) => step.step === currentStep)) {
      setCurrentStep(steps[0].step);
      setMaxUnlockedStep(steps[0].step);
    }
  }, [steps, currentStep, isFormReadOnlyByCallDate, isSubmitContext]);

  useEffect(() => {
    if (currentStep > maxUnlockedStep) {
      setCurrentStep(maxUnlockedStep);
    }
  }, [currentStep, maxUnlockedStep]);

  useEffect(() => {
    if (isFormReadOnlyByCallDate) return;
    if (lockedSearchFundingFromProgram === null) return;
    if (searchFundingFieldIds.length === 0) return;

    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      searchFundingFieldIds.forEach((fieldId) => {
        if (next[fieldId] !== lockedSearchFundingFromProgram) {
          next[fieldId] = lockedSearchFundingFromProgram;
          changed = true;
        }
      });
      if (!changed) return prev;
      return next;
    });

    setDirtyFields((prev) => {
      const next = new Set(prev);
      searchFundingFieldIds.forEach((fieldId) => next.add(fieldId));
      return next;
    });
  }, [isFormReadOnlyByCallDate, lockedSearchFundingFromProgram, searchFundingFieldIds]);

  useEffect(() => {
    if (!shouldSyncStepInUrl) return;
    if (!stepFromUrl) return;
    if (!steps.some((step) => step.step === stepFromUrl)) return;
    if (lastAppliedUrlStepRef.current === stepFromUrl) return;
    lastAppliedUrlStepRef.current = stepFromUrl;
    isApplyingUrlStepRef.current = true;
    setMaxUnlockedStep((prev) => Math.max(prev, stepFromUrl));
    setCurrentStep(stepFromUrl);
  }, [shouldSyncStepInUrl, stepFromUrl, steps]);

  useEffect(() => {
    if (!isEditMode || !shouldSyncStepInUrl) return;
    if (isApplyingUrlStepRef.current) {
      isApplyingUrlStepRef.current = false;
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    const raw = params.get("step");
    const urlStep = raw ? Number(raw) : NaN;
    if (Number.isFinite(urlStep) && urlStep === currentStep) return;
    params.set("step", `${currentStep}`);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  }, [currentStep, isEditMode, navigate, searchParams, shouldSyncStepInUrl]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const moveToStep = useCallback(
    (nextStep: number) => {
      setMaxUnlockedStep((prev) => Math.max(prev, nextStep));
      setCurrentStep(nextStep);

      if (!shouldSyncStepInUrl) return;

      const params = new URLSearchParams(searchParams.toString());
      params.set("step", `${nextStep}`);
      lastAppliedUrlStepRef.current = nextStep;
      navigate({ search: `?${params.toString()}` }, { replace: true });
    },
    [navigate, searchParams, shouldSyncStepInUrl],
  );

  useEffect(() => {
    if (openValidate) return;
    if (!openValidateUser) return;
    if (!isTruthyFlag(currentProjectDetail?.searchFinancingInternal)) {
      setOpenValidateUser(false);
      setValidateUserFromProjectModal(false);
    }
  }, [currentProjectDetail?.searchFinancingInternal, openValidate, openValidateUser]);

  const allFieldsForStep = useMemo(() => {
    if (!effectiveCurrentStepData) return [];
    const orderedRootFields = sortFieldsByOrder(effectiveCurrentStepData.fields ?? []);
    const sectionFields = sortSectionsByOrder(effectiveCurrentStepData.sections ?? []).flatMap(
      (section) => sortFieldsByOrder(section.fields ?? []),
    );
    return [...orderedRootFields, ...sectionFields].filter(
      (field) => !isHiddenCallCandidateField(field),
    );
  }, [effectiveCurrentStepData, isHiddenCallCandidateField]);

  const rootFields = useMemo(
    () =>
      sortFieldsByOrder(effectiveCurrentStepData?.fields ?? []).filter(
        (field) => !isHiddenCallCandidateField(field),
      ),
    [effectiveCurrentStepData, isHiddenCallCandidateField],
  );
  const stepSections = useMemo(
    () =>
      sortSectionsByOrder(effectiveCurrentStepData?.sections ?? []).map((section) => ({
        ...section,
        fields: sortFieldsByOrder(section.fields ?? []).filter(
          (field) => !isHiddenCallCandidateField(field),
        ),
      })),
    [effectiveCurrentStepData, isHiddenCallCandidateField],
  );

  const isDescriptifStep = useMemo(
    () =>
      normalize(effectiveCurrentStepData?.name ?? "") === "descriptif_du_projet"
      || normalize(effectiveCurrentStepData?.label ?? "") === "descriptif du projet",
    [effectiveCurrentStepData],
  );

  const descriptifNativeFields = useMemo(
    () =>
      rootFields.filter((field) => {
        const nativeKey = normalize(field.nativeField ?? field.name ?? "");
        return NATIVE_DESCRIPTIF_KEYS.has(nativeKey);
      }),
    [rootFields],
  );

  const descriptifOtherFields = useMemo(
    () =>
      rootFields.filter((field) => {
        const nativeKey = normalize(field.nativeField ?? field.name ?? "");
        return !NATIVE_DESCRIPTIF_KEYS.has(nativeKey);
      }),
    [rootFields],
  );

  const descriptifDescriptionField = useMemo(
    () =>
      descriptifNativeFields.find(
        (field) => normalize(field.nativeField ?? field.name ?? "") === "description",
      ) ?? null,
    [descriptifNativeFields],
  );

  const descriptifNativeFieldsWithoutDescription = useMemo(
    () =>
      descriptifNativeFields.filter(
        (field) => normalize(field.nativeField ?? field.name ?? "") !== "description",
      ),
    [descriptifNativeFields],
  );

  const updateValue = (fieldKey: string, fieldValue: any) => {
    setValues((prev) => ({ ...prev, [fieldKey]: fieldValue }));
    setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
    setDirtyFields((prev) => new Set(prev).add(fieldKey));
  };

  const normalizeAnswerValueForField = useCallback(
    (fieldId: number, rawValue: any) => {
      const field = fieldById.get(Number(fieldId));
      if (!field) return rawValue ?? null;
      const fieldType = normalize(field.type ?? "");

      if (fieldType === "plantedspecies" || fieldType === "protectedspecies") {
        return toSpeciesItems(rawValue);
      }

      return rawValue ?? null;
    },
    [fieldById],
  );

  const isMissingRequiredValue = (field: ApiField, value: any) => {
    const normalizedType = normalize(field.type ?? "");
    if (isSearchFundingNativeField(field)) {
      return value !== true && value !== false;
    }

    if (normalizedType === "map") {
      const mapValue = toMapFieldValue(value);
      const count = mapValue.drawn.polygons.length > 0
        ? mapValue.drawn.polygons.length
        : mapValue.parcel.length + mapValue.location.length;
      return count === 0;
    }

    if (normalizedType === "checkbox") {
      return !Array.isArray(value) || value.length === 0;
    }

    if (normalizedType === "upload") {
      const uploads = toUploadMedias(value);
      return uploads.length === 0;
    }

    if (normalizedType === "funding") {
      return (
        !hasAnyFundingValue(value) &&
        !hasProjectFundingValue &&
        !customGuidedFundingHasLocalValue
      );
    }

    if (normalizedType === "plantedspecies" || normalizedType === "protectedspecies") {
      const speciesItems = toSpeciesItems(value);
      return speciesItems.length === 0;
    }

    return value === undefined || value === null || `${value}`.trim() === "";
  };

  const saveStepPayload = useCallback(
    async (
      answers: Array<{ fieldId: number; value: any }>,
      options: { introductionPassed?: boolean } = {},
    ) => {
      if (isEditMode) {
        return postData({
          url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/project/${projectId}/save`,
          skipCacheRefresh: true,
          data: {
            answers,
            ...(options.introductionPassed ? { introductionPassed: true } : {}),
          },
        });
      }

      return postData({
        url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/save-step`,
        skipCacheRefresh: true,
        data: {
          draftId,
          programId: selectedProgramId,
          answers,
          ...(options.introductionPassed ? { introductionPassed: true } : {}),
        },
      });
    },
    [draftId, isEditMode, postData, projectId, selectedProgramId],
  );

  const saveDraftChanges = useCallback(
    async (
      fieldKeys: string[] = [],
      forceProgramSync = false,
      options: { introductionPassed?: boolean } = {},
    ): Promise<boolean> => {
      if (!draftId) return false;

      const requestedFieldKeys = [...new Set(fieldKeys)];
      const skippedLegacyFundingKeys = requestedFieldKeys.filter(
        shouldSkipLegacyFundingDraftSave,
      );
      const uniqueFieldKeys = requestedFieldKeys.filter(
        (key) => !shouldSkipLegacyFundingDraftSave(key),
      );
      if (skippedLegacyFundingKeys.length > 0) {
        setDirtyFields((prev) => {
          const next = new Set(prev);
          skippedLegacyFundingKeys.forEach((key) => next.delete(key));
          return next;
        });
      }
      if (
        uniqueFieldKeys.length === 0 &&
        !programDirty &&
        !forceProgramSync &&
        !options.introductionPassed
      ) {
        return true;
      }

      const answers = uniqueFieldKeys.map((key) => {
        const fieldId = Number(key);
        return {
          fieldId,
          value: normalizeAnswerValueForField(fieldId, values[key]),
        };
      });

      setIsSavingDraft(true);
      const result = await saveStepPayload(answers, options);
      setIsSavingDraft(false);

      if (!result?.success) {
        return false;
      }

      if (result?.projectStatus !== undefined && result?.projectStatus !== null) {
        syncProjectStatus(result.projectStatus, result);
      }

      setDirtyFields((prev) => {
        if (uniqueFieldKeys.length === 0) return prev;
        const next = new Set(prev);
        uniqueFieldKeys.forEach((key) => next.delete(key));
        return next;
      });
      setProgramDirty(false);

      return true;
    },
    [draftId, normalizeAnswerValueForField, programDirty, saveStepPayload, shouldSkipLegacyFundingDraftSave, values],
  );

  const persistSingleField = useCallback(
    async (fieldKey: string, fieldValue: any): Promise<boolean> => {
      const savePromise = (async (): Promise<boolean> => {
        if (!draftId) return false;
        const numericFieldId = Number(fieldKey);
        if (!Number.isFinite(numericFieldId) || numericFieldId <= 0) {
          toast({
            className: "text-red-500",
            title: `Veuillez v\u00E9rifier la configuration du champ cartographique (${fieldKey}).`,
          });
          return false;
        }

        console.log("[MAP][save-step][request]", {
          draftId,
          programId: selectedProgramId,
          fieldId: numericFieldId,
          fieldValue,
        });

        const result = await saveStepPayload([
          {
            fieldId: numericFieldId,
            value: normalizeAnswerValueForField(numericFieldId, fieldValue),
          },
        ]);

        if (!result?.success) {
          console.log("[MAP][save-step][failed]", {
            draftId,
            fieldId: numericFieldId,
            apiResult: result,
          });
          toast({
            className: "text-red-500",
            title: "Veuillez r\u00E9essayer : \u00E9chec de la sauvegarde du champ cartographique.",
          });
          return false;
        }

        console.log("[MAP][save-step][success]", {
          draftId,
          fieldId: numericFieldId,
          savedCount: result?.savedCount,
          skippedFieldIds: result?.skippedFieldIds,
          resolvedFieldIds: result?.resolvedFieldIds,
          apiResult: result,
        });

        setDirtyFields((prev) => {
          const next = new Set(prev);
          next.delete(fieldKey);
          return next;
        });
        return true;
      })();

      return trackFieldSave(fieldKey, savePromise);
    },
    [draftId, normalizeAnswerValueForField, saveStepPayload, selectedProgramId, toast, trackFieldSave],
  );

  const saveManualScore = useCallback(
    async (fieldId: number): Promise<boolean> => {
      if (!isEditMode || !projectId) return false;

      const key = String(fieldId);
      const rawValue = manualScoreValues[key] ?? "";
      const scoringEntry = scoringBreakdownByFieldId.get(fieldId);
      const maxPoints = Number(scoringEntry?.maxPoints ?? 0);

      if (`${rawValue}`.trim() !== "") {
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue) || numericValue < 0 || numericValue > maxPoints) {
          setManualScoreErrors((prev) => ({
            ...prev,
            [key]: `La note attribuée doit être comprise entre 0 et ${maxPoints.toFixed(2)}.`,
          }));
          return false;
        }
      }

      setManualScoreErrors((prev) => ({ ...prev, [key]: "" }));
      setSavingManualScores((prev) => ({ ...prev, [key]: true }));

      const result = await validationMutation.postData({
        url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/project/${projectId}/manual-scoring`,
        data: {
          scores: [
            {
              fieldId,
              points: rawValue === "" ? null : Number(rawValue),
            },
          ],
        },
      });

      setSavingManualScores((prev) => ({ ...prev, [key]: false }));

      if (!result?.success) {
        toast({
          className: "text-red-500",
          title: "La note manuelle n'a pas pu être enregistrée.",
        });
        return false;
      }

      if (result?.scoringSummary !== undefined) {
        setLiveScoringSummary(result.scoringSummary ?? null);
      }
      if (result?.scoringSettings !== undefined) {
        setLiveScoringSettings(result.scoringSettings ?? null);
      }
      if (result?.scoringDecision !== undefined) {
        setLiveScoringDecision(result.scoringDecision ?? null);
      }
      if (result?.manualEvaluationCompleted !== undefined) {
        setManualEvaluationCompleted(Boolean(result.manualEvaluationCompleted));
      }

      void projectDetails.refetch();
      toast({
        title: "Évaluation enregistrée.",
      });
      return true;
    },
    [draft, isEditMode, manualScoreValues, projectDetails, projectId, scoringBreakdownByFieldId, toast, validationMutation],
  );

  const saveAllManualScores = useCallback(async (): Promise<boolean> => {
    if (!isEditMode || !projectId || manualScoringEntries.length === 0) {
      return true;
    }

    const nextErrors: Record<string, string> = {};
    let hasValidationError = false;
    const scores = manualScoringEntries.map(([fieldId]) => {
      const rawValue = manualScoreValues[String(fieldId)] ?? "";
      const maxPoints = Number(scoringBreakdownByFieldId.get(fieldId)?.maxPoints ?? 0);
      if (`${rawValue}`.trim() !== "") {
        const numericValue = Number(rawValue);
        if (!Number.isFinite(numericValue) || numericValue < 0 || numericValue > maxPoints) {
          nextErrors[String(fieldId)] = `La note attribuée doit être comprise entre 0 et ${maxPoints.toFixed(2)}.`;
          hasValidationError = true;
        } else {
          nextErrors[String(fieldId)] = "";
        }
      } else {
        nextErrors[String(fieldId)] = "";
      }
      return {
        fieldId,
        points: `${rawValue}`.trim() === "" ? null : Number(rawValue),
      };
    });

    setManualScoreErrors((prev) => ({ ...prev, ...nextErrors }));
    if (hasValidationError) {
      toast({
        className: "text-red-500",
        title: "La note attribuée ne doit pas être négative ni supérieure au barème.",
      });
      return false;
    }

    const savingState = Object.fromEntries(
      manualScoringEntries.map(([fieldId]) => [String(fieldId), true]),
    ) as Record<string, boolean>;
    setSavingManualScores((prev) => ({ ...prev, ...savingState }));

    const result = await validationMutation.postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/project/${projectId}/manual-scoring`,
      data: { scores },
    });

    setSavingManualScores((prev) => {
      const next = { ...prev };
      manualScoringEntries.forEach(([fieldId]) => {
        next[String(fieldId)] = false;
      });
      return next;
    });

    if (!result?.success) {
      toast({
        className: "text-red-500",
        title: "Les notes manuelles n'ont pas pu être enregistrées.",
      });
      return false;
    }

    if (result?.scoringSummary !== undefined) {
      setLiveScoringSummary(result.scoringSummary ?? null);
    }
    if (result?.scoringSettings !== undefined) {
      setLiveScoringSettings(result.scoringSettings ?? null);
    }
    if (result?.scoringDecision !== undefined) {
      setLiveScoringDecision(result.scoringDecision ?? null);
    }
    if (result?.manualEvaluationCompleted !== undefined) {
      setManualEvaluationCompleted(Boolean(result.manualEvaluationCompleted));
    }

    void projectDetails.refetch();
    return true;
  }, [
    draft,
    isEditMode,
    manualScoreValues,
    manualScoringEntries,
    projectDetails,
    projectId,
    scoringBreakdownByFieldId,
    toast,
    validationMutation,
  ]);

  const completeManualEvaluation = useCallback(async () => {
    if (!projectId || !isEditMode) return;

    setIsCompletingEvaluation(true);
    const result = await validationMutation.postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/project/${projectId}/manual-scoring/complete`,
      data: {},
    });
    setIsCompletingEvaluation(false);

    if (!result?.success) {
      if (result?.scoringSummary !== undefined) {
        setLiveScoringSummary(result.scoringSummary ?? null);
      }
      if (result?.scoringSettings !== undefined) {
        setLiveScoringSettings(result.scoringSettings ?? null);
      }
      if (result?.scoringDecision !== undefined) {
        setLiveScoringDecision(result.scoringDecision ?? null);
      }
      if (result?.manualEvaluationCompleted !== undefined) {
        setManualEvaluationCompleted(Boolean(result.manualEvaluationCompleted));
      }
      if (result?.scoringSummary) {
        setOpenIncompleteEvaluationModal(true);
        return;
      }
      toast({
        className: "text-red-500",
        title: result?.message ?? "L'évaluation n'a pas pu être finalisée.",
      });
      return;
    }

    const completedProjectStatus = result?.projectStatus ?? result?.status ?? null;
    if (completedProjectStatus !== undefined && completedProjectStatus !== null) {
      syncProjectStatus(completedProjectStatus, result);
    }
    if (result?.scoringSummary !== undefined) {
      setLiveScoringSummary(result.scoringSummary ?? null);
    }
    if (result?.scoringSettings !== undefined) {
      setLiveScoringSettings(result.scoringSettings ?? null);
    }
    if (result?.scoringDecision !== undefined) {
      setLiveScoringDecision(result.scoringDecision ?? null);
    }
    if (result?.manualEvaluationCompleted !== undefined) {
      setManualEvaluationCompleted(Boolean(result.manualEvaluationCompleted));
    }

    setIsEvaluationMode(false);
    toast({
      title: "Évaluation terminée.",
    });

    if (
      projectId
      && Number(completedProjectStatus) === projectStatusEnum.Validated
    ) {
      window.location.assign(`/dashboard/project/${projectId}?tab=synthetic`);
      return;
    }

    void projectDetails.refetch();
  }, [draft, isEditMode, projectDetails, projectId, toast, validationMutation]);

  const handleFinishEvaluationAttempt = useCallback(() => {
    const run = async () => {
      if (localManualPendingCount > 0) {
        setOpenIncompleteEvaluationModal(true);
        return;
      }

      const saveSucceeded = await saveAllManualScores();
      if (!saveSucceeded) {
        return;
      }

      await completeManualEvaluation();
    };

    void run();
  }, [completeManualEvaluation, localManualPendingCount, saveAllManualScores]);

  const persistFieldImmediately = useCallback(
    async (fieldKey: string, fieldValue: any): Promise<boolean> => {
      const savePromise = (async (): Promise<boolean> => {
        if (!draftId) return false;
        const numericFieldId = Number(fieldKey);
        if (!Number.isFinite(numericFieldId) || numericFieldId <= 0) {
          return false;
        }
        if (shouldSkipLegacyFundingDraftSave(fieldKey)) {
          setDirtyFields((prev) => {
            const next = new Set(prev);
            next.delete(fieldKey);
            return next;
          });
          return true;
        }

        const result = await saveStepPayload([
          {
            fieldId: numericFieldId,
            value: normalizeAnswerValueForField(numericFieldId, fieldValue),
          },
        ]);

        if (!result?.success) {
          return false;
        }

        if (result?.projectStatus !== undefined && result?.projectStatus !== null) {
          syncProjectStatus(result.projectStatus, result);
        }

        setDirtyFields((prev) => {
          const next = new Set(prev);
          next.delete(fieldKey);
          return next;
        });
        return true;
      })();

      return trackFieldSave(fieldKey, savePromise);
    },
    [draftId, normalizeAnswerValueForField, saveStepPayload, shouldSkipLegacyFundingDraftSave, trackFieldSave],
  );

  const persistFieldOnBlur = useCallback(
    async (fieldKey: string) => {
      if (!draftId) return;
      await trackFieldSave(fieldKey, saveDraftChanges([fieldKey]));
    },
    [draftId, saveDraftChanges, trackFieldSave],
  );

  const validateCurrentStep = () => {
    const nextErrors: Record<string, string> = {};

    allFieldsForStep.forEach((field) => {
      const key = `${field.id}`;
      const value = values[key];

      if (!field.required && !isSearchFundingNativeField(field)) return;
      if (isMissingRequiredValue(field, value)) {
        nextErrors[key] = "Champ obligatoire";
      }
    });

    if (!isEditMode && !selectedProgramId) {
      nextErrors.__program = "Programme obligatoire";
    }

    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };


  const goToNextStep = async () => {
    if (areProjectAnswersReadOnly) {
      const currentIndex = steps.findIndex((step) => step.step === currentStep);
      const nextStep =
        currentIndex >= 0 && currentIndex < steps.length - 1
          ? steps[currentIndex + 1].step
          : currentStep;
      if (nextStep !== currentStep) {
        moveToStep(nextStep);
      }
      return;
    }
    if (!validateCurrentStep()) return;

    const currentStepDefinition = steps.find((step) => step.step === currentStep);
    const currentStepFieldKeys = allFieldsForStep.map((field) => `${field.id}`);
    const pendingSavesOk = await waitForPendingFieldSaves(currentStepFieldKeys);
    if (!pendingSavesOk) {
      toast({
        className: "text-red-500",
        title: "Veuillez r\u00E9essayer : une erreur est survenue lors de la sauvegarde du champ modifi\u00E9.",
      });
      return;
    }

    const stepKeys = currentStepFieldKeys.filter((fieldKey) =>
      dirtyFieldsRef.current.has(fieldKey),
    );
    const introductionPassed = currentStepDefinition
      ? isIntroStep(currentStepDefinition)
      : false;
    const shouldSyncProgram = !isEditMode && programDirty;
    const shouldSaveStep =
      stepKeys.length > 0 || shouldSyncProgram || introductionPassed;
    const ok = shouldSaveStep
      ? await saveDraftChanges(stepKeys, shouldSyncProgram, {
          introductionPassed,
        })
      : true;
    if (!ok) {
      toast({
        className: "text-red-500",
        title: "Veuillez r\u00E9essayer : une erreur est survenue lors de la sauvegarde du brouillon.",
      });
      return;
    }

    const currentIndex = steps.findIndex((step) => step.step === currentStep);
    const nextStep =
      currentIndex >= 0 && currentIndex < steps.length - 1
        ? steps[currentIndex + 1].step
        : currentStep;
    if (nextStep === currentStep) {
      return;
    }
    moveToStep(nextStep);
  };

  const handleSubmit = async () => {
    if (isFormReadOnlyByCallDate) return;
    if (!validateCurrentStep()) return;
    if (!draftId) return;

    const pendingKeys = Array.from(dirtyFields);
    const saved = await saveDraftChanges(pendingKeys, true);
    if (!saved) {
      toast({
        className: "text-red-500",
        title: "Veuillez r\u00E9essayer : impossible de finaliser la sauvegarde du brouillon.",
      });
      return;
    }

    if (isEditMode) {
      if (isEditableDraftProject && projectId) {
        const previousStatus = Number(projectStatus ?? currentProjectDetail?.status);
        const optimisticStatus = projectStatusEnum.AwaitingValidationPP;
        syncProjectStatus(optimisticStatus, {
          id: projectId,
          projectId,
          status: optimisticStatus,
          projectStatus: optimisticStatus,
        });

        const submitResult = await postData({
          url: `${import.meta.env.VITE_API_ENDPOINT}/project/send-in/${projectId}`,
          data: {},
        });

        if (!submitResult || (submitResult?.id === undefined && submitResult?.success !== true)) {
          if (Number.isFinite(previousStatus)) {
            syncProjectStatus(previousStatus, {
              id: projectId,
              projectId,
              status: previousStatus,
              projectStatus: previousStatus,
            });
          }
          toast({
            className: "text-red-500",
            title: "Veuillez r\u00E9essayer : la soumission du projet a \u00E9chou\u00E9.",
          });
          return;
        }

        const nextStatus =
          submitResult?.status ??
          submitResult?.projectStatus ??
          optimisticStatus;
        const submittedProjectPayload = {
          ...submitResult,
          id: submitResult?.id ?? projectId,
          projectId,
          status: nextStatus,
          projectStatus: nextStatus,
        };
        syncProjectStatus(nextStatus, submittedProjectPayload);

        toast({
          title: "Le projet a \u00E9t\u00E9 soumis avec succ\u00E8s.",
        });
        returnToListPath();
        return;
      }

      toast({
        title: "Le projet a \u00E9t\u00E9 mis \u00E0 jour avec succ\u00E8s.",
      });
      returnToListPath();
      return;
    }

    const result = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/submit`,
      data: {
        draftId,
        programId: selectedProgramId,
      },
    });

    if (!result?.success) {
      toast({
        className: "text-red-500",
        title: "Veuillez v\u00E9rifier les informations saisies : la cr\u00E9ation du projet a \u00E9chou\u00E9.",
      });
      return;
    }

    toast({
      title: "Le projet a \u00E9t\u00E9 cr\u00E9\u00E9 avec succ\u00E8s.",
    });
    returnToListPath();
  };


  const handleResetDraft = async () => {
    if (!draftId) {
      toast({
        className: "text-red-500",
        title: "Veuillez r\u00E9essayer : aucun brouillon actif n'a \u00E9t\u00E9 trouv\u00E9.",
      });
      return;
    }

    setIsResettingDraft(true);
    const result = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project-form-draft/reset`,
      data: {
        draftId,
      },
    });
    setIsResettingDraft(false);

    if (!result?.success) {
      toast({
        className: "text-red-500",
        title: "Veuillez r\u00E9essayer : impossible de r\u00E9initialiser le formulaire.",
      });
      return;
    }

    toast({
      title: "Le formulaire a \u00E9t\u00E9 r\u00E9initialis\u00E9 avec succ\u00E8s.",
    });
    setOpenResetModal(false);
    window.location.reload();
  };

  const handleValidateProjectStatus = async (validate: boolean) => {
    if (!currentProjectDetail?.id) return;

    setLoadingValidate(true);

    const searchFinancingInternal = currentProjectDetail.searchFinancingInternal;
    let nextStatus = projectStatusEnum.AwaitingValidation;
    if (
      Number(resolvedProjectStatus) === projectStatusEnum.AwaitingValidationPP
      && isFalseFlag(searchFinancingInternal)
      && validate
    ) {
      nextStatus = projectStatusEnum.AwaitingValidation;
    } else if (
      Number(resolvedProjectStatus) === projectStatusEnum.AwaitingValidationPP
      && isTruthyFlag(searchFinancingInternal)
      && validate
    ) {
      nextStatus = projectStatusEnum.Validated;
    } else if (
      Number(resolvedProjectStatus) === projectStatusEnum.AwaitingValidationPP
      && !validate
    ) {
      nextStatus = projectStatusEnum.Rejected;
    }

    const previousStatus = Number(projectStatus ?? currentProjectDetail?.status);
    syncProjectStatus(nextStatus, {
      id: currentProjectDetail.id,
      projectId: currentProjectDetail.id,
      status: nextStatus,
      projectStatus: nextStatus,
    });

    const result = await validationMutation.postData({
      url: `${import.meta.env.VITE_IP_API}/api/desired_funding/validate`,
      data: {
        id_project: currentProjectDetail.id,
        status: nextStatus,
      },
    });

    setLoadingValidate(false);
    setOpenValidate(false);
    setOpenReject(false);

    if (!result) {
      if (Number.isFinite(previousStatus)) {
        syncProjectStatus(previousStatus, {
          id: currentProjectDetail.id,
          projectId: currentProjectDetail.id,
          status: previousStatus,
          projectStatus: previousStatus,
        });
      }
      toast({
        className: "text-red-500",
        title: "La validation du projet a \u00E9chou\u00E9.",
      });
      return;
    }

    const validatedProjectStatus = result?.projectStatus ?? result?.status ?? nextStatus;
    syncProjectStatus(validatedProjectStatus, result);
    if (
      validate &&
      Number(validatedProjectStatus) === projectStatusEnum.Validated &&
      isProjectCreatorCpIntervenant
    ) {
      setCreatorValidationSuccess(true);
    }
    toast({
      title: "Modification enregistr\u00E9e.",
    });

    if (validate && Number(validatedProjectStatus) === projectStatusEnum.Validated) {
      window.location.assign(`/dashboard/project/${currentProjectDetail.id}?tab=synthetic`);
      return;
    }
  };

  const handleValidateProjectCreator = async () => {
    if (!currentProjectDetail?.creator?.id) return;

    setLoadingValidateUser(true);
    const result = await validationMutation.postData({
      url: `${import.meta.env.VITE_IP_API}/api/users/call-project/validate`,
      data: {
        id_user: currentProjectDetail.creator.id,
      },
    });
    setLoadingValidateUser(false);
    setOpenValidateUser(false);
    setValidateUserFromProjectModal(false);

    if (!result) {
      toast({
        className: "text-red-500",
        title: "La validation du candidat a \u00E9chou\u00E9.",
      });
      return;
    }

    setCreatorValidationSuccess(true);
    toast({
      title: "Candidat valid\u00E9.",
    });
  };

  const handleResetCandidateProjectToInProgress = async () => {
    if (!currentProjectDetail?.id) return;

    setLoadingResetInProgress(true);
    const previousStatus = Number(projectStatus ?? currentProjectDetail?.status);

    syncProjectStatus(projectStatusEnum.InProgress, {
      id: currentProjectDetail.id,
      projectId: currentProjectDetail.id,
      status: projectStatusEnum.InProgress,
      projectStatus: projectStatusEnum.InProgress,
    });

    const result = await validationMutation.postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/project/reset-candidate-in-progress/${currentProjectDetail.id}`,
      data: {},
    });

    setLoadingResetInProgress(false);

    if (!result) {
      if (Number.isFinite(previousStatus)) {
        syncProjectStatus(previousStatus, {
          id: currentProjectDetail.id,
          projectId: currentProjectDetail.id,
          status: previousStatus,
          projectStatus: previousStatus,
        });
      }
      toast({
        className: "text-red-500",
        title: "Impossible de remettre le projet en cours.",
      });
      return;
    }

    syncProjectStatus(projectStatusEnum.InProgress, result);
    setOpenResetInProgress(false);
    toast({
      title: "Le projet a été remis en cours.",
    });
    window.location.reload();
  };

  const renderField = (field: ApiField) => {
    const key = `${field.id}`;
    const value = values[key];
    const commonClass = "w-full";
    const normalizedType = normalize(field.type ?? "");

    if (normalizedType === "textarea") {
      return (
        <Textarea
          className={commonClass}
          value={value ?? ""}
          disabled={areProjectAnswersReadOnly}
          onChange={(e) => updateValue(key, e.target.value)}
          onBlur={() => {
            persistFieldOnBlur(key);
          }}
          placeholder={field.label}
        />
      );
    }

    if (normalizedType === "select") {
      const options = Array.isArray(field.options) ? field.options : [];
      const isSearchFundingField = isSearchFundingNativeField(field);
      const isProjectTypeNativeField = isProjectTypeField(field);
      const isSearchFundingLocked =
        isSearchFundingField && lockedSearchFundingFromProgram !== null;
      const isProjectTypeLocked =
        isProjectTypeNativeField &&
        (Boolean(lockedProjectTypeId) || isCpIntervenant || isEditMode);
      const effectiveValue =
        isSearchFundingField &&
          (value === null || value === undefined || value === "") &&
          lockedSearchFundingFromProgram !== null
          ? lockedSearchFundingFromProgram
          : value;
      const rawDisplayedValue = isSearchFundingField
        ? formatSearchFundingValueForSelect(effectiveValue, options)
        : (value ?? "");
      const displayedValue =
        options.includes(String(rawDisplayedValue))
          ? String(rawDisplayedValue)
          : "";
      const showPlaceholder = normalizedType === "select";
      return (
        <select
          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={displayedValue}
          disabled={areProjectAnswersReadOnly || isSearchFundingLocked || isProjectTypeLocked}
          onChange={async (e) => {
            if (isSearchFundingField) {
              const nextValue = parseSearchFundingValue(e.target.value);
              updateValue(key, nextValue);
              await persistFieldImmediately(key, nextValue);
              return;
            }
            const nextValue = e.target.value === "" ? null : e.target.value;
            updateValue(key, nextValue);
            await persistFieldImmediately(key, nextValue);
          }}
        >
          {showPlaceholder && (
            <option value="" disabled>
              Selectionner
            </option>
          )}
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (normalizedType === "radio") {
      const options = Array.isArray(field.options) ? field.options : [];
      const current = value ?? "";
      return (
        <div className="space-y-2">
          {options.map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={`field-${field.id}-radio`}
                checked={String(current) === String(option)}
                disabled={areProjectAnswersReadOnly}
                onClick={async (e) => {
                  if (String(current) !== String(option)) return;
                  e.preventDefault();
                  updateValue(key, null);
                  await persistFieldImmediately(key, null);
                }}
                onChange={async () => {
                  updateValue(key, option);
                  await persistFieldImmediately(key, option);
                }}
              />
              {option}
            </label>
          ))}
        </div>
      );
    }

    if (normalizedType === "checkbox") {
      const options = Array.isArray(field.options) ? field.options : [];
      const current = Array.isArray(value) ? value : [];
      return (
        <div className="space-y-2">
          {options.map((option) => {
            const checked = current.includes(option);
            return (
              <label key={option} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={areProjectAnswersReadOnly}
                  onChange={async (e) => {
                    if (e.target.checked) {
                      const nextValue = [...current, option];
                      updateValue(key, nextValue);
                      await persistFieldImmediately(key, nextValue);
                    } else {
                      const nextValue = current.filter((item: string) => item !== option);
                      updateValue(key, nextValue);
                      await persistFieldImmediately(key, nextValue);
                    }
                  }}
                />
                {option}
              </label>
            );
          })}
        </div>
      );
    }

    if (isProjectTypeField(field)) {
      return (
        <SearchableSelect
          value={value ?? ""}
          options={projectTypeOptions}
          placeholder="S\u00E9lectionner un type"
          searchPlaceholder="Rechercher un type de projet..."
          emptyText="Aucun type de projet disponible."
          disabled={
            areProjectAnswersReadOnly ||
            Boolean(lockedProjectTypeId) ||
            isCpIntervenant ||
            isEditMode ||
            (projectTypes.loading && projectTypeOptions.length === 0)
          }
          loadingLabel="Chargement des types de projet..."
          onChange={(nextValue) => {
            updateValue(key, nextValue);
            void persistFieldImmediately(key, nextValue);
          }}
        />
      );
    }

    if (normalizedType === "country") {
      return (
        <SearchableSelect
          value={value ?? ""}
          options={countryOptions}
          placeholder="Sélectionner un pays"
          searchPlaceholder="Rechercher un pays..."
          emptyText="Aucun pays disponible."
          disabled={areProjectAnswersReadOnly || (countries.loading && countryOptions.length === 0)}
          loadingLabel="Chargement des pays..."
          onChange={(nextValue) => {
            updateValue(key, nextValue);
            void persistFieldImmediately(key, nextValue);
          }}
        />
      );
    }

    if (normalizedType === "upload") {
      const medias = toUploadMedias(value);
      return (
        <div className={`space-y-2 ${areProjectAnswersReadOnly ? "pointer-events-none opacity-80" : ""}`}>
          <CustomFileUploader
            type={2}
            medias={medias}
            afterChange={(newMedias: any) => {
              const incoming = Array.isArray(newMedias) ? newMedias : [];
              const merged = [...medias, ...incoming];
              const uniqueById = merged.reduce((acc: any[], media: any) => {
                if (!media?.id) return acc;
                if (!acc.some((m) => m.id === media.id)) {
                  acc.push(media);
                }
                return acc;
              }, []);
              updateValue(key, uniqueById);
              void persistFieldImmediately(key, uniqueById);
            }}
            dropZoneConfig={{
              maxFiles: 20,
              multiple: true,
            }}
            onDelete={(id: number) => {
              const nextValue = medias.filter((media: any) => media?.id !== id);
              updateValue(key, nextValue);
              void persistFieldImmediately(key, nextValue);
            }}
            size="md"
            textTypes="PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG"
            sizeLimite={10}
            afterEdit={(editedMedia: any) => {
              const nextValue = medias.map((media: any) => (media?.id === editedMedia?.id ? editedMedia : media));
              updateValue(key, nextValue);
              void persistFieldImmediately(key, nextValue);
            }}
            defaultType={2}
          />
        </div>
      );
    }

    if (normalizedType === "map") {
      const mapValue = toMapFieldValue(value);
      return (
        <div className={areProjectAnswersReadOnly ? "opacity-90" : ""}>
          <DrawnMapField
            value={mapValue.drawn}
            readOnly={areProjectAnswersReadOnly}
            onChange={(nextDrawn) => {
              const serialized = serializeDrawnAnswer(nextDrawn);
              console.log("[MAP][onChange][drawn-updated]", {
                fieldId: key,
                polygons: nextDrawn?.polygons?.length ?? 0,
                serialized,
              });
              updateValue(key, serialized);
              void persistSingleField(key, serialized);
            }}
          />
        </div>
      );
    }

    if (normalizedType === "plantedspecies") {
      const selected = toSpeciesItems(value);
      return (
        <div className={areProjectAnswersReadOnly ? "pointer-events-none opacity-80" : ""}>
          <PlantInput
          handleChange={async (items) => {
            const nextValue = items.map((item) => ({
              name: item.name,
              nameFr: item.nameFr,
              nameEn: item.nameEn,
              id: item.id,
            }));
            updateValue(key, nextValue);
            const saved = await persistFieldImmediately(key, nextValue);
            if (!saved) {
              toast({
                className: "text-red-500",
                title: "Veuillez r\u00E9essayer : les essences plant\u00E9es n'ont pas pu \u00EAtre enregistr\u00E9es.",
              });
            }
          }}
          selected={selected}
          withMargin={false}
          speciesType={2}
          />
        </div>
      );
    }

    if (normalizedType === "protectedspecies") {
      const selected = toSpeciesItems(value);
      return (
        <div className={areProjectAnswersReadOnly ? "pointer-events-none opacity-80" : ""}>
          <PlantInput
          handleChange={async (items) => {
            const nextValue = items.map((item) => ({
              name: item.name,
              nameFr: item.nameFr,
              nameEn: item.nameEn,
              id: item.id,
            }));
            updateValue(key, nextValue);
            const saved = await persistFieldImmediately(key, nextValue);
            if (!saved) {
              toast({
                className: "text-red-500",
                title: "Veuillez r\u00E9essayer : les esp\u00E8ces prot\u00E9g\u00E9es n'ont pas pu \u00EAtre enregistr\u00E9es.",
              });
            }
          }}
          selected={selected}
          withMargin={false}
          speciesType={1}
          />
        </div>
      );
    }

    if (normalizedType === "funding") {
      const native = normalize(field.nativeField ?? field.name ?? "");
      const mode = native.includes("obtained") ? "obtained" : "desired";
      return (
        <div className={areProjectAnswersReadOnly ? "pointer-events-none opacity-80" : ""}>
          <FundingField
            mode={mode}
            value={value}
            deviseOptions={deviseOptions}
            defaultDeviseId={defaultFundingDeviseId}
            onChange={(items) => {
              updateValue(key, items);
              void persistFieldImmediately(key, items);
            }}
          />
        </div>
      );
    }

    return (
      <Input
        className={commonClass}
        type={normalizedType === "number" ? "number" : normalizedType === "date" ? "date" : "text"}
        value={value ?? ""}
        disabled={areProjectAnswersReadOnly}
        onChange={(e) => updateValue(key, e.target.value)}
        onBlur={() => {
          persistFieldOnBlur(key);
        }}
        placeholder={field.label}
      />
    );
  };

  const renderFieldBlock = (field: ApiField) => (
    <div
      key={field.id}
      className={normalize(field.type ?? "") === "map" || normalize(field.type ?? "") === "upload" ? "space-y-2 md:col-span-2" : "space-y-2"}
    >
      {(() => {
        const scoringItem = showPerFieldScores
          ? scoringBreakdownByFieldId.get(Number(field.id))
          : null;
        const isManualScoring = scoringItem?.mode === "manual" && scoringItem?.includedInPercentage;
        const manualScoreKey = String(field.id);
        return (
          <>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <Label htmlFor={`field-${field.id}`} className="mb-1 block text-sm font-medium text-[#475a44]">
                {field.label} {field.required ? <span className="text-red-500">*</span> : null}
              </Label>
              {scoringItem?.includedInPercentage && (
                isManualScoring ? (
                  <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-medium text-sky-700">
                    {scoringItem.pendingReview || scoringItem.points === null || scoringItem.points === undefined
                      ? `À évaluer manuellement / ${Number(scoringItem.maxPoints ?? 0).toFixed(2)}`
                      : `${Number(scoringItem.points ?? 0).toFixed(2)} / ${Number(scoringItem.maxPoints ?? 0).toFixed(2)}`}
                  </span>
                ) : (
                  <span className="rounded-full bg-[#edf6e7] px-2.5 py-1 text-[11px] font-medium text-[#486a2f]">
                    {Number(scoringItem.points ?? 0).toFixed(2)} / {Number(scoringItem.maxPoints ?? 0).toFixed(2)}
                  </span>
                )
              )}
            </div>
            {renderField(field)}
            {isManualScoring && shouldShowManualScoringInputs && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-sky-800">
                      Évaluation manuelle
                    </p>
                    <p className="text-xs text-sky-700">
                      Attribuez une note entre 0 et {Number(scoringItem.maxPoints ?? 0).toFixed(2)} pour cette question.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="w-[120px]">
                      <Input
                        type="number"
                        min={0}
                        max={Number(scoringItem.maxPoints ?? 0)}
                        step="0.01"
                        value={manualScoreValues[manualScoreKey] ?? ""}
                        onChange={(e) => {
                          setManualScoreErrors((prev) => ({
                            ...prev,
                            [manualScoreKey]: "",
                          }));
                          setManualScoreValues((prev) => ({
                            ...prev,
                            [manualScoreKey]: e.target.value,
                          }));
                        }}
                        onBlur={() => {
                          void saveManualScore(Number(field.id));
                        }}
                        placeholder="Note"
                        className="bg-white"
                      />
                    </div>
                    <span className="pb-2 text-xs font-medium text-sky-800">
                      / {Number(scoringItem.maxPoints ?? 0).toFixed(2)}
                    </span>
                    {savingManualScores[manualScoreKey] && (
                      <span className="inline-flex items-center gap-1 pb-2 text-xs text-sky-700">
                        <Loader2 className="size-3 animate-spin" />
                        Enregistrement...
                      </span>
                    )}
                  </div>
                </div>
                {manualScoreErrors[manualScoreKey] && (
                  <p className="mt-2 text-xs text-red-500">{manualScoreErrors[manualScoreKey]}</p>
                )}
              </div>
            )}
          </>
        );
      })()}
      {errors[`${field.id}`] && (
        <p className="text-xs text-red-500">{errors[`${field.id}`]}</p>
      )}
    </div>
  );

  const getFundingTabValue = (
    field: ApiField,
    mode: FundingMode,
    storeCombinedValue: boolean,
  ) => {
    const rawValue = values[`${field.id}`];

    if (!storeCombinedValue) {
      return rawValue;
    }

    if (isCombinedFundingValue(rawValue)) {
      return normalizeFundingModeValue(rawValue, mode);
    }

    const nativeMode = getFundingNativeMode(field);
    if (!nativeMode) {
      return mode === "desired" ? rawValue : [];
    }

    return nativeMode === mode ? rawValue : [];
  };

  const updateFundingTabValue = (
    field: ApiField,
    mode: FundingMode,
    items: FundingItem[],
    storeCombinedValue: boolean,
  ) => {
    const key = `${field.id}`;

    if (!storeCombinedValue) {
      updateValue(key, items);
      void persistFieldImmediately(key, items);
      return;
    }

    const currentRawValue = values[key];
    const nextValue: Record<FundingMode, FundingItem[]> = {
      obtained: mode === "obtained"
        ? items
        : getFundingTabValue(field, "obtained", true),
      desired: mode === "desired"
        ? items
        : getFundingTabValue(field, "desired", true),
    };

    if (!isCombinedFundingValue(currentRawValue)) {
      const nativeMode = getFundingNativeMode(field);
      if (nativeMode && nativeMode !== mode) {
        nextValue[nativeMode] = normalizeFundingValue(currentRawValue);
      }
    }

    updateValue(key, nextValue);
    void persistFieldImmediately(key, nextValue);
  };

  const renderFundingFieldForMode = (
    field: ApiField,
    mode: FundingMode,
    storeCombinedValue: boolean,
  ) => (
    <div className={areProjectAnswersReadOnly ? "pointer-events-none opacity-80" : ""}>
      <FundingField
        mode={mode}
        value={getFundingTabValue(field, mode, storeCombinedValue)}
        deviseOptions={deviseOptions}
        defaultDeviseId={defaultFundingDeviseId}
        onChange={(items) => {
          updateFundingTabValue(field, mode, items, storeCombinedValue);
        }}
      />
    </div>
  );

  const getResolvedFundingConfiguration = (fields: ApiField[]) => {
    const fundingFields = fields.filter((field) => normalize(field.type ?? "") === "funding");
    if (fundingFields.length === 0) return null;
    const singleFundingField = fundingFields.length === 1 ? fundingFields[0] : null;
    const storeCombinedValue = Boolean(singleFundingField);

    const obtainedField = fundingFields.find((field) => {
      const native = normalize(field.nativeField ?? field.name ?? "");
      return native.includes("obtained");
    });
    const desiredField = fundingFields.find((field) => {
      const native = normalize(field.nativeField ?? field.name ?? "");
      return native.includes("desired");
    });

    const fallbackFields = fundingFields.filter(
      (field) => field.id !== obtainedField?.id && field.id !== desiredField?.id,
    );
    const resolvedObtained = singleFundingField ?? obtainedField ?? fallbackFields[0];
    const resolvedDesired = singleFundingField ?? desiredField ?? fallbackFields[1];

    if (!resolvedObtained && !resolvedDesired) return null;

    return {
      resolvedObtained,
      resolvedDesired,
      storeCombinedValue,
    };
  };

  const renderFundingTabs = (fields: ApiField[]) => {
    const fundingConfiguration = getResolvedFundingConfiguration(fields);
    if (!fundingConfiguration) return null;

    const { resolvedObtained, resolvedDesired, storeCombinedValue } = fundingConfiguration;
    const activeFundingTab =
      fundingTab === "obtainedFunding" && !resolvedObtained
        ? "desiredFunding"
        : fundingTab === "desiredFunding" && !resolvedDesired
          ? "obtainedFunding"
          : fundingTab;

    return (
      <div className="md:col-span-2">
        <Tabs value={activeFundingTab} className="mt-2 w-full" onValueChange={setFundingTab}>
          <TabsList className="border-primary w-full items-end justify-start rounded-none border-b bg-white p-0">
            {resolvedObtained && (
              <TabsTrigger
                value="obtainedFunding"
                className="sm:text-md border-primary me-4 rounded-none ps-0 text-xs data-[state=active]:border-b-4 data-[state=active]:bg-white"
              >
                Financements déjà obtenus
              </TabsTrigger>
            )}
            {resolvedDesired && (
              <TabsTrigger
                value="desiredFunding"
                className="sm:text-md border-primary rounded-none ps-0 text-xs data-[state=active]:border-b-4 data-[state=active]:bg-white"
              >
                Financements souhaités
              </TabsTrigger>
            )}
          </TabsList>

          {resolvedObtained && (
            <TabsContent value="obtainedFunding" className="pt-4">
              <div className="space-y-2">
                {renderFundingFieldForMode(resolvedObtained, "obtained", storeCombinedValue)}
                {errors[`${resolvedObtained.id}`] && (
                  <p className="text-xs text-red-500">{errors[`${resolvedObtained.id}`]}</p>
                )}
              </div>
            </TabsContent>
          )}

          {resolvedDesired && (
            <TabsContent value="desiredFunding" className="pt-4">
              <div className="space-y-2">
                {renderFundingFieldForMode(resolvedDesired, "desired", storeCombinedValue)}
                {errors[`${resolvedDesired.id}`] && (
                  <p className="text-xs text-red-500">{errors[`${resolvedDesired.id}`]}</p>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    );
  };

  const renderDraftFundingGuidedFlow = (fields: ApiField[]) => {
    const fundingConfiguration = getResolvedFundingConfiguration(fields);
    if (!fundingConfiguration) return null;

    const fundingErrorFieldIds = Array.from(
      new Set(
        [fundingConfiguration.resolvedObtained?.id, fundingConfiguration.resolvedDesired?.id].filter(
          (id): id is number => Number.isFinite(Number(id)) && Number(id) > 0,
        ),
      ),
    );
    const hasFundingErrors = fundingErrorFieldIds.some((fieldId) => Boolean(errors[`${fieldId}`]));

    if (isEditMode && !currentProjectDetail?.id) {
      return (
        <div className="md:col-span-2 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <Skeleton className="mb-3 h-5 w-44" />
          <Skeleton className="mb-4 h-4 w-80 max-w-full" />
          <div className="grid gap-3 md:grid-cols-3">
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
          </div>
        </div>
      );
    }

    if (!currentProjectDetail?.id) {
      return renderFundingTabs(fields);
    }

    return (
      <div className="md:col-span-2">
        <Suspense
          fallback={(
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <Skeleton className="mb-3 h-5 w-44" />
              <Skeleton className="mb-4 h-4 w-80 max-w-full" />
              <div className="grid gap-3 md:grid-cols-3">
                <Skeleton className="h-28 rounded-2xl" />
                <Skeleton className="h-28 rounded-2xl" />
                <Skeleton className="h-28 rounded-2xl" />
              </div>
            </div>
          )}
        >
          <ProjectFundingGuidedFlow
            project={currentProjectDetail}
            total={projectFundingTotal}
            isReadOnly={areProjectAnswersReadOnly}
            actualCurrency={projectFundingCurrency}
            symboleDevise={projectFundingCurrencySymbol}
            displayObtainedFundings={displayedProjectObtainedFundings}
            displayDesiredFundings={displayedProjectDesiredFundings}
            showExistingFundingCurrencySelector
            onExistingFundingCurrencyChange={setProjectFundingCurrencySelection}
            onGoNext={() => void goToNextStep()}
            onCompletionChange={(completed) => {
              if (completed) {
                setCustomGuidedFundingHasLocalValue(true);
              }
            }}
          />
        </Suspense>
        {hasFundingErrors ? (
          <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Veuillez renseigner au moins un financement avant de continuer.
          </div>
        ) : null}
      </div>
    );
  };

  const renderFieldsWithFunding = (fields: ApiField[]) => {
    const nonFundingFields = fields.filter((field) => normalize(field.type ?? "") !== "funding");
    const hasFunding = fields.some((field) => normalize(field.type ?? "") === "funding");

    return (
      <>
        {nonFundingFields.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {nonFundingFields.map(renderFieldBlock)}
          </div>
        )}
        {hasFunding && <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{renderDraftFundingGuidedFlow(fields)}</div>}
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[#e1e8dc] bg-white p-6">
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="mb-4 h-10 w-full" />
        <Skeleton className="mb-3 h-5 w-1/3" />
        <Skeleton className="mb-4 h-10 w-full" />
        <Skeleton className="mb-3 h-5 w-1/2" />
        <Skeleton className="mb-4 h-24 w-full" />
        <div className="flex justify-between">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-40" />
        </div>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="rounded-2xl border border-[#e1e8dc] bg-white p-6">
        <h1 className="text-lg font-semibold text-[#2f3f2d]">
          {isEditMode ? "Fiche projet" : "Nouveau projet"}
        </h1>
        <p className="mt-2 text-sm text-[#5f6d5c]">
          Le formulaire est en cours d&apos;initialisation. Veuillez rafraichir la page dans quelques secondes.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#e1e8dc] bg-white p-5 md:p-6">
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-[#2f3f2d]">
          {isEditMode ? "Fiche projet" : "Nouveau projet"}
        </h1>
        <div className="mt-1 flex items-center gap-2 text-sm text-[#5f6d5c]">
          <Link to={listPath} className="hover:underline">
            Mes projets
          </Link>
          <ChevronRightIcon className="size-4" />
          <span className="font-medium text-orange-500">
            {isEditMode ? resolvedProjectDisplayName : "Fiche projet"}
          </span>
        </div>
        {!isEditMode && (
          <div className="mt-4">
            <Button
              type="button"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              onClick={() => setOpenResetModal(true)}
              disabled={isFormReadOnlyByCallDate}
            >
              Réinitialiser le formulaire
            </Button>
          </div>
        )}
      </div>

      <div className="mb-6 rounded-xl border border-[#e8ede4] bg-[#f8faf6] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 leading-none">
            <FolderOpen className="size-5 shrink-0 self-center text-[#486a2f]" />
            {isEditMode ? (
              <p className="m-0 text-lg font-semibold tracking-wide leading-none text-[#2f3f2d]">
                {`Projet - ${resolvedProjectDisplayName}`}
              </p>
            ) : selectedProgram || programNameFromUrl || selectedProgramFromAll ? (
              <p className="m-0 text-lg font-semibold tracking-wide leading-none text-[#2f3f2d]">
                {`Programme - ${selectedProgram?.name ?? programNameFromUrl ?? selectedProgramFromAll?.name
                  }`}
              </p>
            ) : programIdFromUrl || sidebar.loading || programsInfo.loading ? (
              <div className="inline-flex items-center gap-2 text-lg font-semibold tracking-wide leading-none text-[#2f3f2d]">
                <Loader2 className="size-4 animate-spin text-[#486a2f]" />
                <span>Chargement du programme...</span>
              </div>
            ) : (
              <p className="m-0 text-lg font-semibold tracking-wide leading-none text-[#2f3f2d]">
                Programme - Non disponible
              </p>
            )}
          </div>
          {isEditMode && projectStatusBadge && (
            <div className="flex shrink-0 flex-col items-end gap-2">
              <span className={`rounded-full px-2 py-1 text-xs ${projectStatusBadge.className}`}>
                {projectStatusBadge.label}
              </span>
              {!isSubmitContext && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setIsExportDialogOpen(true)}
                  className="h-8 rounded-full border-[#cfdcc8] bg-white px-3 text-xs font-semibold text-[#315429] shadow-sm hover:border-[#315429] hover:bg-[#f3f8f0]"
                >
                  <Download className="mr-1.5 size-3.5" />
                  Exporter
                </Button>
              )}
            </div>
          )}
        </div>
        {shouldShowCarrierDecisionButtons && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => setOpenValidate(true)}
                disabled={projectDetails.loading}
              >
                {projectDetails.loading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  "Valider le projet"
                )}
              </Button>
              <Button
                type="button"
                className="bg-verdeored hover:bg-verdeored/90"
                onClick={() => setOpenReject(true)}
                disabled={projectDetails.loading}
              >
                {projectDetails.loading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  "Rejeter le projet"
                )}
              </Button>
            </div>
        )}
        {isEditMode
          && isCarrierUser
          && Number(resolvedProjectStatus) === projectStatusEnum.Validated
          && canValidateCandidate && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => {
                  setValidateUserFromProjectModal(false);
                  setOpenValidateUser(true);
                }}
                disabled={projectDetails.loading}
              >
                {projectDetails.loading ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Chargement...
                  </>
                ) : (
                  "Valider le candidat"
                )}
              </Button>
            </div>
          )}
        {canResetCandidateProjectToInProgress && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpenResetInProgress(true)}
              disabled={loadingResetInProgress || projectDetails.loading}
              className="h-8 rounded-full border-[#cfdcc8] bg-white px-3 text-xs font-semibold text-[#315429] shadow-sm hover:border-[#315429] hover:bg-[#f3f8f0]"
            >
              {loadingResetInProgress ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-1.5 size-3.5" />
                  Remettre en cours
                </>
              )}
            </Button>
          </div>
        )}
        {errors.__program && <p className="mt-1 text-xs text-red-500">{errors.__program}</p>}
        {displayedCallDeadline && (
          <div
            className={`mt-3 inline-flex items-center gap-1.5 text-xs ${
              isFormReadOnlyByCallDate ? "text-red-700" : "text-[#4f6544]"
            }`}
          >
            <Clock3 className="size-3.5 shrink-0" />
            <span>
              <span className="font-medium">Date limite de dépôt :</span> {displayedCallDeadline}
            </span>
          </div>
        )}
        {isFormReadOnlyByCallDate && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600" />
            <div className="space-y-1">
              <p className="font-semibold text-red-900">Dépôt des candidatures clôturé</p>
              <p>
                La période de dépôt des candidatures est terminée. Toute modification ou soumission
                de projet n’est plus possible.
              </p>
            </div>
          </div>
        )}
        {shouldShowDashboardScoringHeader && (
          <div className={`mt-4 rounded-2xl border px-4 py-4 shadow-sm ${scoringTheme.ring}`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2 leading-none">
                  <span className="text-sm font-semibold leading-none text-[#2f3f2d]">Notation du projet</span>
                  <span className={`inline-flex items-center self-center rounded-full px-2.5 py-1 text-[11px] font-medium leading-none ${scoringTheme.badge}`}>
                    {getScoringDecisionLabel(
                      scoringDecision?.decision ?? null,
                      scoringDecision?.decisionMode ?? scoringSettings?.decisionMode ?? null,
                    )}
                  </span>
                </div>
                <p className="text-xs text-[#5f6d5c]">
                  {getScoringModeLabel(
                    scoringDecision?.decisionMode ?? scoringSettings?.decisionMode ?? null,
                  )}
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-[#5f6d5c]">
                  {scoringSettings?.rejectionPercentage !== null
                    && scoringSettings?.rejectionPercentage !== undefined && (
                    <span className="rounded-full bg-white/80 px-2.5 py-1">
                      Rejet auto jusqu’à {formatScorePercent(scoringSettings.rejectionPercentage)}
                    </span>
                  )}
                  {scoringSettings?.acceptancePercentage !== null
                    && scoringSettings?.acceptancePercentage !== undefined && (
                    <span className="rounded-full bg-white/80 px-2.5 py-1">
                      Validation auto à partir de {formatScorePercent(scoringSettings.acceptancePercentage)}
                    </span>
                  )}
                </div>
                {effectiveManualPendingCount > 0 && (
                  <p className={`text-sm font-medium ${scoringTheme.accent}`}>
                    En attente de l&apos;évaluation du porteur de projet.
                  </p>
                )}
                {canEvaluateManually && effectiveManualIncludedCount > 0 && !hasStartedOrCompletedEvaluation && (
                  <div className="pt-1">
                    <Button
                      type="button"
                      size="sm"
                      className="bg-sky-600 text-white hover:bg-sky-700"
                      onClick={() => setIsEvaluationMode(true)}
                    >
                      Commencer l&apos;évaluation
                    </Button>
                  </div>
                )}
                {shouldShowFinishEvaluationAction && (
                  <div className="pt-1">
                    <Button
                      type="button"
                      size="sm"
                      className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                      disabled={isCompletingEvaluation}
                      onClick={handleFinishEvaluationAttempt}
                    >
                      {isCompletingEvaluation ? "Finalisation..." : "Terminer l'évaluation"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-4 rounded-2xl bg-white/80 px-4 py-3 shadow-sm">
                <div className="min-w-[120px] text-center">
                  <p className={`text-4xl font-bold tracking-tight ${scoringTheme.score}`}>
                    {formatScorePercent(scoringDisplayPercentage)}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#6b7c66]">
                    Score final
                  </p>
                </div>
                <div className="space-y-1 border-l border-[#dbe3d5] pl-4 text-xs text-[#5f6d5c]">
                  <p>
                    Points automatique :{" "}
                    <span className="font-medium text-[#2f3f2d]">
                      {Number(scoringSummary.autoScore ?? 0).toFixed(2)} /{" "}
                      {Number(scoringSummary.autoMaxScore ?? 0).toFixed(2)}
                    </span>
                  </p>
                  <p>
                    Points attribués manuellement :{" "}
                    <span className="font-medium text-[#2f3f2d]">
                      {Number(scoringSummary.manualReviewedScore ?? 0).toFixed(2)} /{" "}
                      {manualScoreMax.toFixed(2)}
                    </span>
                  </p>
                  <p>
                    Note finale :{" "}
                    <span className="font-medium text-[#2f3f2d]">
                      {Number(scoringSummary.totalCurrentScore ?? 0).toFixed(2)} /{" "}
                      {Number(scoringSummary.totalIncludedMaxScore ?? 0).toFixed(2)}
                    </span>
                  </p>
                  {effectiveManualPendingCount > 0 && (
                    <p className="text-amber-700">
                      {effectiveManualPendingCount} question(s) restent à noter
                    </p>
                  )}
                  {showPerFieldScores && effectiveManualReviewedCount > 0 && (
                    <p>
                      Nombre de questions évaluées :{" "}
                      <span className="font-medium text-[#2f3f2d]">
                        {effectiveManualReviewedCount} / {effectiveManualIncludedCount}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {!shouldHideProjectFormUntilEvaluationStarts && (
        <>
          <div className="mb-5 flex flex-wrap gap-2">
            {steps.map((step) => (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  if (step.step <= maxUnlockedStep) {
                    setMaxUnlockedStep((prev) => Math.max(prev, step.step));
                    setCurrentStep(step.step);
                    return;
                  }
                  toast({
                    className: "text-red-500",
                    title: "Veuillez d'abord valider l'\u00E9tape pr\u00E9c\u00E9dente.",
                  });
                }}
                className={`rounded-full px-3 py-1 text-xs ${step.step === currentStep ? "bg-[#486a2f] text-white" : "bg-[#edf2e8] text-[#4d5f49]"
                  }`}
              >
                {step.step}. {step.label}
              </button>
            ))}
          </div>

          {effectiveCurrentStepData && (
            <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#30422f]">{effectiveCurrentStepData.label}</h2>
          {currentStepDescription.trim() && (
            hasHtmlContent(currentStepDescription) ? (
              <div
                className="break-words overflow-x-auto text-sm text-[#5f6d5c] [&_a]:text-[#3b6f2a] [&_a]:underline [&_a]:hover:text-[#2f5a22] [&_img]:mx-auto [&_img]:block [&_img]:h-auto [&_img]:max-w-full [&_table]:max-w-full [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                dangerouslySetInnerHTML={{ __html: currentStepDescription }}
              />
            ) : (
              <p className="break-words text-sm text-[#5f6d5c]">
                {renderTextWithAutoLinks(currentStepDescription)}
              </p>
            )
          )}

          {!isDescriptifStep && rootFields.length > 0 && (
            renderFieldsWithFunding(rootFields)
          )}

          {isDescriptifStep && descriptifNativeFields.length > 0 && (
            <>
              {renderFieldsWithFunding(descriptifNativeFieldsWithoutDescription)}
              {descriptifDescriptionField ? (
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {renderFieldBlock(descriptifDescriptionField)}
                  <div />
                </div>
              ) : (
                null
              )}
            </>
          )}

          {isDescriptifStep && descriptifOtherFields.length > 0 && (
            <div>
              <div className="my-5 border-t border-[#dbe3d5]" />
              <h3 className="mb-4 text-sm font-semibold tracking-wide text-[#30422f]">Informations compl\u00E9mentaires</h3>
              {renderFieldsWithFunding(descriptifOtherFields)}
            </div>
          )}

          {stepSections.map((section) => (
            <div
              key={section.id}
              className="rounded-xl border border-[#e8ede4] bg-[#f8faf6] p-4"
            >
              <h3 className="mb-4 text-sm font-semibold tracking-wide text-[#30422f]">
                {section.label}
              </h3>
              {String(section.description ?? "").trim() && (
                hasHtmlContent(String(section.description)) ? (
                  <div
                    className="mb-4 break-words overflow-x-auto text-sm text-[#5f6d5c] [&_a]:text-[#3b6f2a] [&_a]:underline [&_a]:hover:text-[#2f5a22] [&_img]:mx-auto [&_img]:block [&_img]:h-auto [&_img]:max-w-full [&_table]:max-w-full [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1"
                    dangerouslySetInnerHTML={{ __html: String(section.description) }}
                  />
                ) : (
                  <p className="mb-4 break-words text-sm text-[#5f6d5c]">
                    {renderTextWithAutoLinks(String(section.description))}
                  </p>
                )
              )}
              {renderFieldsWithFunding(section.fields ?? [])}
            </div>
          ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={currentStep <= 1}
          onClick={() => {
            const currentIndex = steps.findIndex(
              (step) => step.step === currentStep,
            );
            if (currentIndex <= 0) return;
            const previousStep = steps[currentIndex - 1].step;
            setMaxUnlockedStep((prev) => Math.max(prev, previousStep));
            setCurrentStep(previousStep);
          }}
        >
          <ChevronLeft className="mr-1 size-4" />
          Précédent
        </Button>

        <div className="flex items-center gap-3">
          {(isSavingDraft || dirtyFields.size > 0 || programDirty) && (
            <span className="inline-flex items-center gap-1 text-xs text-[#5f6d5c]">
              <Clock3 className="size-3" />
              Sauvegarde du brouillon...
            </span>
          )}

          {!isLastStep ? (
            <Button type="button" onClick={goToNextStep}>
              Suivant
              <ChevronRight className="ml-1 size-4" />
            </Button>
          ) : shouldShowFinishEvaluationAction ? (
            <Button
              type="button"
              onClick={handleFinishEvaluationAttempt}
              disabled={isCompletingEvaluation}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {isCompletingEvaluation ? "Finalisation..." : "Terminer l'évaluation"}
            </Button>
          ) : areProjectAnswersReadOnly ? null : (
            <Button type="button" onClick={handleSubmit} disabled={loading || isSavingDraft}>
              {loading
                ? (isEditMode ? "Sauvegarde..." : "Cr\u00E9ation...")
                : (isEditableDraftProject || !isEditMode ? "Soumettre le projet" : "Enregistrer les modifications")}
              <Save className="ml-1 size-4" />
            </Button>
          )}
          </div>
          </div>
        </>
      )}

      {!isEditMode && (
        <Dialog open={openResetModal} onOpenChange={setOpenResetModal}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle>Confirmer la r\u00E9initialisation</DialogTitle>
              <DialogDescription>
                Cette action r\u00E9initialise enti\u00E8rement le formulaire \u00E0 partir de la version actuelle du sch\u00E9ma.
              </DialogDescription>
            </DialogHeader>
            <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="size-4 shrink-0 text-amber-500" />
              <span>Toutes les informations saisies seront perdues.</span>
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenResetModal(false)}>
                Annuler
              </Button>
              <Button type="button" onClick={handleResetDraft} disabled={isResettingDraft}>
                {isResettingDraft ? "R\u00E9initialisation..." : "Confirmer la r\u00E9initialisation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {!isSubmitContext && (
        <CandidateProjectExportDialog
          isOpen={isExportDialogOpen}
          onOpenChange={setIsExportDialogOpen}
          isExporting={isExportingProject}
          onExport={handleProjectExport}
        />
      )}

      <Dialog open={openValidate} onOpenChange={setOpenValidate}>
        <DialogContent className="sm:max-w-[600px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleValidateProjectStatus(true);
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle>Valider le projet</DialogTitle>
              <DialogDescription>
                En cliquant sur valider, vous confirmez que les changements correspondent bien à vos besoins.
                {isFalseFlag(currentProjectDetail?.searchFinancingInternal) && (
                  <>
                    <br />
                    Après votre validation, Verdeo effectuera une vérification et une validation finale.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="submit" disabled={loadingValidate}>
                {loadingValidate ? "Validation..." : "Valider"}
              </Button>
              <Button type="button" onClick={() => setOpenValidate(false)} className="bg-slate-500">
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openReject} onOpenChange={setOpenReject}>
        <DialogContent className="sm:max-w-[600px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleValidateProjectStatus(false);
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle>Rejeter le projet</DialogTitle>
              <DialogDescription>
                En cliquant sur Rejeter, vous confirmez votre décision et ce projet sera marqué comme rejeté.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="submit" disabled={loadingValidate}>
                {loadingValidate ? "Rejet..." : "Rejeter"}
              </Button>
              <Button
                type="button"
                onClick={() => setOpenReject(false)}
                className="bg-slate-500"
              >
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openResetInProgress} onOpenChange={setOpenResetInProgress}>
        <DialogContent className="sm:max-w-[600px]">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleResetCandidateProjectToInProgress();
            }}
            className="space-y-6"
          >
            <DialogHeader>
              <DialogTitle>Remettre le projet en cours</DialogTitle>
              <DialogDescription>
                Cette action remet ce projet candidat au statut En cours afin de poursuivre son traitement.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="submit" disabled={loadingResetInProgress}>
                {loadingResetInProgress ? "Mise à jour..." : "Confirmer"}
              </Button>
              <Button
                type="button"
                onClick={() => setOpenResetInProgress(false)}
                className="bg-slate-500"
              >
                Annuler
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openValidateUser} onOpenChange={setOpenValidateUser}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Valider le candidat</DialogTitle>
            <DialogDescription>
              Souhaitez-vous valider le candidat ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => void handleValidateProjectCreator()} disabled={loadingValidateUser}>
              {loadingValidateUser ? "Validation..." : "Valider le candidat"}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpenValidateUser(false);
                setValidateUserFromProjectModal(false);
              }}
              className="bg-slate-500"
            >
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openIncompleteEvaluationModal} onOpenChange={setOpenIncompleteEvaluationModal}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Évaluation incomplète</DialogTitle>
            <DialogDescription>
              Toutes les questions nécessitant une note manuelle doivent être évaluées avant de terminer l’évaluation du projet.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {effectiveManualPendingCount > 0 ? (
              <span>
                Il reste encore <strong>{effectiveManualPendingCount}</strong> question(s) à évaluer manuellement.
              </span>
            ) : (
              <span>Merci de vérifier les notes manuelles avant de poursuivre.</span>
            )}
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setOpenIncompleteEvaluationModal(false)}>
              Compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CallForProjectsCreateProject;
